// React Query hooks wrapping Supabase reads (and direct writes for kb_articles / sla_rules / team_members / ticket_watchers).
// All access is RLS-gated — the browser never uses the service role.
//
// Types are derived from `database.types.ts`, which is generated from the
// live Supabase schema via `pnpm typegen`. To regenerate after a schema
// migration, run that command and commit the diff.

import { useMemo } from 'react';
import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { supabase } from './supabase';
import {
  JOURNEY_CSV_MAX,
  JOURNEY_PAGE_SIZE,
  periodStart,
  searchOrClause,
  sortSpec,
  type JourneyFilters,
} from './journeys-filters';
import type { ReportBucket, ReportMetrics } from './reports';
import {
  CONTACT_CSV_MAX,
  CONTACT_PAGE_SIZE,
  contactSearchClause,
  contactTicketSubjectTerm,
  type ContactDirectory,
  type ContactDirectoryRow,
  type ContactFilters,
  type ContactOverviewData,
} from './contacts';
import type { Database } from './database.types';
import type { RuleCondition, RuleAction, RuleMatchType } from './rule-engine';
import { useDebounce } from './use-debounce';
import { parseAccess, type AccessPayload, type AuditRow } from './permissions';
import {
  buildSearchRpcArgs,
  hasSearchContent,
  parseSearchQuery,
  parseSearchResponse,
  type SearchRpcArgs,
  type TicketSearchPage,
} from './search-query';
import { selectContactSuggestions } from './contact-suggestions';
import { chunk, pageAll } from './page-all';
import {
  MY_DAY_ACTIVE_STATUSES,
  MY_DAY_OPEN_STATUSES,
  RESOLVED_WINDOW_MS,
  UP_FOR_GRABS_FETCH,
  mergeTicketRows,
} from './my-day-tickets';
import {
  applyReadState,
  applyReadStateWhere,
  bumpTypeCount,
  countByType,
  matchesView,
  typesForTab,
  type NotificationTabId,
  type NotificationType,
  type UnreadTypeCounts,
} from './notification-categories';

// Convenience aliases for the table Row types. Refining a few unknowns to
// stricter unions where it's safe (e.g. ticket_messages.direction).
type Tables = Database['public']['Tables'];

export type Ticket = Tables['tickets']['Row'];
export type TicketEvent = Tables['ticket_events']['Row'];

// Tighten the .direction union on TicketMessage — the DB has a CHECK
// constraint but the generated types render it as `string`.
export type TicketMessage = Omit<Tables['ticket_messages']['Row'], 'direction'> & {
  direction: 'inbound' | 'outbound';
};

export type KbArticle = Tables['kb_articles']['Row'];
export type SlaRule = Tables['sla_rules']['Row'];
// Generated database.types hasn't been regenerated since migration 032
// added the `role` column + email_notification_prefs (031). Widen here so
// the SPA gets type safety on the new fields without depending on a fresh
// type-gen run.
export type TeamRole = 'admin' | 'member';
export type TeamMember = Tables['team_members']['Row'] & {
  role: TeamRole;
  email_notification_prefs?: Record<string, boolean> | null;
  // Migration 035 — invitation status tracking.
  invited_at?: string | null;
  /** First successful sign-in timestamp. NULL = pending invitation. */
  joined_at?: string | null;
};

// Contact = the contacts_cache row, surfaced under a friendlier name.
export type Contact = Tables['contacts_cache']['Row'];

// Notification.type is a CHECK-constrained text column. The union lives in
// `notification-categories.ts` (a zero-import module the root vitest suite can
// load) next to the type → tab mapping; re-exported here so the existing
// `import { type NotificationType } from '@/lib/queries'` call sites keep working.
export type { NotificationType } from './notification-categories';

export type Notification = Omit<Tables['notifications']['Row'], 'type'> & {
  type: NotificationType;
};

export type TicketNote = Tables['ticket_notes']['Row'];
/** Reply-template visibility — see migration 053. */
export type ReplyTemplateVisibility = 'shared' | 'private';

/** Reply template row with the visibility column narrowed to the
 *  CHECK-constrained enum. Other columns flow through unchanged. */
export type ReplyTemplate = Omit<Tables['reply_templates']['Row'], 'visibility'> & {
  visibility: ReplyTemplateVisibility;
};
export type CompanySettings = Tables['company_settings']['Row'];

// client_journeys.trigger is also CHECK-constrained — narrow it.
export type ClientJourney = Omit<Tables['client_journeys']['Row'], 'trigger' | 'payload'> & {
  trigger: 'manual' | 'auto_30d_inactivity';
  payload: Record<string, unknown>;
};

// -------------------- Filters --------------------

/** Columns the date-range filter can target. All resolve to real
 *  timestamptz columns on `tickets`; the names match the DB exactly so
 *  we can pass the value straight into `q.gte(field, from)`. */
export type TicketDateField =
  | 'created_at'
  | 'updated_at'
  | 'first_response_at'
  | 'resolved_at'
  | 'last_team_message_at'
  | 'last_client_message_at';

/** Human-readable labels for the date field dropdown. Keys here are the
 *  whitelist of valid values — any UI selection that isn't in this map
 *  is rejected (defaults back to `created_at`).
 *
 *  Reply-field semantics (2026-06-12): "Client replied last" / "Team
 *  replied last" use the STRICT "this side spoke MOST RECENTLY"
 *  interpretation. The server-side .gte/.lte does the coarse "fell
 *  within the range" cut; the dashboard's client-side filter then
 *  layers `last_X_message_at >= last_Y_message_at` on top, so a
 *  ticket with status `waiting_on_client` can't match "Client replied
 *  last" (the team's reply made the team the last voice). This
 *  matches what users intuitively want — "the client sent the last
 *  message, it's now our turn" — and what the chip's name implies. */
export const TICKET_DATE_FIELD_LABELS: Record<TicketDateField, string> = {
  created_at: 'Created',
  updated_at: 'Any update',
  first_response_at: 'First reply',
  resolved_at: 'Resolved',
  last_team_message_at: 'Team replied last',
  last_client_message_at: 'Client replied last',
};

export interface TicketFilters {
  /** Only tickets that are unread for me (migration 081). */
  unread?: boolean;
  priorities?: string[];
  statuses?: string[];
  isExistingClient?: boolean;
  /** Restrict to tickets assigned to any of the listed slack_user_ids (OR semantics). Empty/undefined = no filter. */
  assigneeSlackIds?: string[];
  /** Restrict to tickets with these categories (OR semantics). Used by drill-down from /reports. */
  categories?: string[];
  /** ISO 8601 timestamp — lower bound for the chosen `dateField`. Used by
   *  the dashboard date range filter; both bounds are optional so
   *  "this month onward" or "everything up to a date" still work. */
  createdFrom?: string;
  /** ISO 8601 timestamp — upper bound for the chosen `dateField`. */
  createdTo?: string;
  /** Which timestamp column the date range applies to. Defaults to
   *  `created_at` (when the ticket was first opened). The other columns
   *  are all nullable, so picking, say, `resolved_at` naturally
   *  excludes unresolved tickets — that's the correct behaviour
   *  ("tickets resolved this week" shouldn't return open tickets). */
  dateField?: TicketDateField;
  /** Restrict to tickets from these inboxes (OR semantics). Used by the
   *  inbox-scoped routes (/inbox/:slug/dashboard etc.) — they pass a
   *  single-id array. Empty/undefined = no SPA-side filter (RLS still
   *  scopes server-side). */
  inboxIds?: string[];
}

// -------------------- Queries --------------------

// There is deliberately NO "load the newest N tickets" hook any more.
// `useTickets` was `ORDER BY created_at DESC LIMIT 500`, and every list built from
// it silently lost any ticket older than the 500th newest — even one assigned to
// you with the client still replying (found 2026-10-16). Lists now come from the
// server, which does the tab / chips / counts / paging in SQL: the Dashboard from
// search_tickets_v2 (`useTicketSearchV2`), My Day from `useMyDayTickets`,
// "Watching" from `useMyWatchedTickets`. See CODE_MAP.md "Never miss a ticket".

// ── Ticket search (search_tickets_v2, migration 076) ────────────────────────
// ONE server-side search for the dashboard: scoped to the current inbox,
// applies the tab and the chips in SQL, so counts and "Load more" are about
// the matches. See CODE_MAP.md "Ticket search".
export type TicketSearchResult = TicketSearchPage<Ticket>;
/** The RPC's own maximum page (it clamps p_limit to 100). */
export const TICKET_SEARCH_PAGE = 100;

/** Pass null to disable (nothing typed). `args` is built by buildSearchRpcArgs;
 *  paging is added here, so `args` must not carry limit/offset in the key. */
export function useTicketSearchV2(args: SearchRpcArgs | null) {
  return useInfiniteQuery({
    queryKey: ['ticket-search-v2', args],
    enabled: !!args,
    initialPageParam: 0,
    // Keep the previous results on screen while the next keystroke loads.
    placeholderData: (prev) => prev,
    staleTime: 15_000,
    queryFn: async ({ pageParam, signal }): Promise<TicketSearchResult> => {
      const { data, error } = await supabase
        .rpc('search_tickets_v2', {
          ...(args as SearchRpcArgs),
          p_limit: TICKET_SEARCH_PAGE,
          p_offset: pageParam,
        })
        .abortSignal(signal);
      if (error) throw error;
      return parseSearchResponse<Ticket>(data);
    },
    getNextPageParam: (last: TicketSearchResult, all: TicketSearchResult[]) => {
      const loaded = all.reduce((n, pg) => n + pg.rows.length, 0);
      return last.rows.length > 0 && loaded < last.total ? loaded : undefined;
    },
  });
}

/** One page of the same search, fetched directly (no hook): used where a list has to
 *  be read to its end, e.g. the FYI tab's "Clear all" (see lib/search-ids.ts). */
export async function fetchTicketSearchPage(args: SearchRpcArgs, offset: number): Promise<TicketSearchResult> {
  const { data, error } = await supabase.rpc('search_tickets_v2', {
    ...args,
    p_limit: TICKET_SEARCH_PAGE,
    p_offset: offset,
  });
  if (error) throw error;
  return parseSearchResponse<Ticket>(data);
}

/** Ctrl+K palette: the same server search, current inbox, every tab. */
export function usePaletteTicketSearch(inboxId: string | undefined, query: string) {
  const term = useDebounce(query.trim(), 200);
  const parsed = useMemo(() => parseSearchQuery(term), [term]);
  const active = !!inboxId && term.length >= 2 && hasSearchContent(parsed);
  return useQuery<TicketSearchResult>({
    queryKey: ['ticket-search-palette', inboxId, term],
    enabled: active,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
    queryFn: async ({ signal }): Promise<TicketSearchResult> => {
      const { args } = buildSearchRpcArgs(parsed, {
        inboxId: inboxId as string,
        group: 'any',
        filters: {},
        sort: 'relevance',
        mySlackId: null,
        members: [],
        limit: 8,
      });
      const { data, error } = await supabase.rpc('search_tickets_v2', args).abortSignal(signal);
      if (error) throw error;
      return parseSearchResponse<Ticket>(data);
    },
  });
}

/** The palette's empty state: the newest few tickets in this inbox. Explicit
 *  columns, so opening Ctrl+K no longer pulls 500 full rows. */
export type RecentInboxTicket = Pick<
  Ticket,
  'id' | 'subject' | 'sender_email' | 'sender_name' | 'priority' | 'status' | 'created_at'
>;
export function useRecentInboxTickets(inboxId: string | undefined, limit = 5) {
  return useQuery({
    queryKey: ['tickets-recent', inboxId, limit],
    enabled: !!inboxId,
    staleTime: 30_000,
    queryFn: async (): Promise<RecentInboxTicket[]> => {
      const { data, error } = await supabase
        .from('tickets')
        .select('id, subject, sender_email, sender_name, priority, status, created_at')
        .eq('inbox_id', inboxId as string)
        .is('archived_at', null)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return (data ?? []) as RecentInboxTicket[];
    },
  });
}

/** After a ticket changes (claim, resolve, assign…), refresh every list that
 *  can show it: the dashboard page AND any search results. */
export function invalidateTicketLists(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ['tickets'] });
  qc.invalidateQueries({ queryKey: ['ticket-search-v2'] });
  qc.invalidateQueries({ queryKey: ['ticket-search-palette'] });
  qc.invalidateQueries({ queryKey: ['tickets-recent'] });
}

/** invalidateTicketLists plus the badges and side lists it doesn't cover (inbox counts, the FYI
 *  count, watched tickets, a contact's ticket lists). What a ticket action refreshes once
 *  nothing is in flight any more. */
export function refreshTicketListsEverywhere(qc: QueryClient) {
  invalidateTicketLists(qc);
  qc.invalidateQueries({ queryKey: ['inbox-new-counts'] });
  qc.invalidateQueries({ queryKey: ['fyi-count'] });
  qc.invalidateQueries({ queryKey: ['my-watched-tickets'] });
  qc.invalidateQueries({ queryKey: ['contact-tickets'] });
  qc.invalidateQueries({ queryKey: ['contact-recent-tickets'] });
  qc.invalidateQueries({ queryKey: ['contact-tickets-page'] });
}

export function useTicket(ticketId: string | undefined) {
  return useQuery({
    queryKey: ['ticket', ticketId],
    enabled: !!ticketId,
    queryFn: async (): Promise<Ticket | null> => {
      if (!ticketId) return null;
      const { data, error } = await supabase
        .from('tickets')
        // is_automated_sender is a computed column: not in "*", so name it.
        .select('*, is_automated_sender')
        .eq('id', ticketId)
        .maybeSingle();
      if (error) throw error;
      return data as Ticket | null;
    },
  });
}

export function useTicketEvents(ticketId: string | undefined) {
  return useQuery({
    queryKey: ['ticket-events', ticketId],
    enabled: !!ticketId,
    refetchInterval: 15_000,
    queryFn: async (): Promise<TicketEvent[]> => {
      if (!ticketId) return [];
      const { data, error } = await supabase
        .from('ticket_events')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as TicketEvent[];
    },
  });
}

export function useTicketMessages(ticketId: string | undefined) {
  return useQuery({
    queryKey: ['ticket-messages', ticketId],
    enabled: !!ticketId,
    // A client reply or a teammate's answer shows up on an open ticket within 15 s
    // (and feeds the composer's collision warning). Only while the tab is visible.
    refetchInterval: 15_000,
    queryFn: async (): Promise<TicketMessage[]> => {
      if (!ticketId) return [];
      const { data, error } = await supabase
        .from('ticket_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data ?? []) as TicketMessage[];
    },
  });
}

export function useContacts(search?: string) {
  return useQuery({
    queryKey: ['contacts', search ?? ''],
    queryFn: async (): Promise<Contact[]> => {
      let q = supabase
        .from('contacts_cache')
        .select('*')
        .order('fetched_at', { ascending: false })
        .limit(200);
      const clause = contactSearchClause(search ?? '');
      if (clause) q = q.or(clause);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Contact[];
    },
  });
}

/** Powers the To/Cc/Bcc autocomplete dropdown in ReplyComposer + ComposeDialog.
 *  `query` is the live text the user is typing in that field; `excluding` is
 *  the addresses already added to it (so a picked contact doesn't show up
 *  again). Debounces 150ms before hitting contacts_cache, then applies the
 *  length-gate + already-added filter from `selectContactSuggestions`. */
export function useContactSuggestions(query: string, excluding: string[]) {
  const debouncedQuery = useDebounce(query, 150);
  const contactsQ = useContacts(debouncedQuery);
  return useMemo(
    () => selectContactSuggestions(contactsQ.data ?? [], debouncedQuery, excluding),
    [contactsQ.data, debouncedQuery, excluding],
  );
}

/** Single contact by email. Used by the contact detail page so the
 *  editor has the full row (notes, manual-edit flag, etc.) without
 *  filtering the list query. Returns null when there's no cache row
 *  (e.g. a brand-new sender not yet triaged). */
export function useContact(email: string | undefined) {
  return useQuery({
    queryKey: ['contact', email],
    enabled: !!email,
    queryFn: async (): Promise<Contact | null> => {
      if (!email) return null;
      const { data, error } = await supabase
        .from('contacts_cache')
        .select('*')
        .eq('email', email.toLowerCase())
        .maybeSingle();
      if (error) throw error;
      return (data as Contact) ?? null;
    },
  });
}

export interface ContactEdit {
  full_name?: string | null;
  is_existing_client?: boolean;
  client_tier?: string | null;
  // Legacy single note: the UI no longer writes it (notes live in contact_notes).
  notes?: string | null;
}

/** Manually edit a contact. Sets `is_manually_edited = true` so
 *  workflow 01 stops overwriting it from Patients on Demand
 *  (see migration 038 + 01-pipeline.js getContactCachedOrFresh).
 *
 *  Uses UPSERT, not UPDATE: a contact can have tickets but no
 *  contacts_cache row (workflow 01's cache-write swallows errors, so a
 *  transient failure at triage leaves no row). Upsert creates it on
 *  first edit. Allowed by the `team_update_contacts_cache` +
 *  `team_insert_contacts_cache` RLS policies (migrations 038 + 040);
 *  contacts_cache is reference data, not a ticket-bearing table, so
 *  CLAUDE.md §6 doesn't route this through n8n. */
export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { email: string; patch: ContactEdit; editorEmail: string | null }) => {
      const { email, patch, editorEmail } = args;
      const lower = email.toLowerCase();
      const { data, error } = await supabase
        .from('contacts_cache')
        .upsert(
          {
            email: lower,
            // Defaults for a freshly-created row; harmlessly overwritten
            // by `patch` below when the user set them. is_existing_client
            // is NOT NULL so it must always be present.
            is_existing_client: false,
            ...patch,
            is_manually_edited: true,
            edited_by: editorEmail,
            edited_at: new Date().toISOString(),
          },
          { onConflict: 'email' },
        )
        .select()
        .single();
      if (error) throw error;
      return data as Contact;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contact', row.email] });
      // Tickets show is_existing_client / tier inline — refresh so an
      // edited client-status reflects without a hard reload.
      qc.invalidateQueries({ queryKey: ['contact-tickets', row.email] });
      // The DB trigger just appended an audit row — pull it in.
      qc.invalidateQueries({ queryKey: ['contact-edit-log', row.email] });
      qc.invalidateQueries({ queryKey: ['contact-directory'] });
      qc.invalidateQueries({ queryKey: ['contact-search'] });
      qc.invalidateQueries({ queryKey: ['contact-tickets-page'] });
      qc.invalidateQueries({ queryKey: ['contact-recent-tickets'] });
    },
  });
}

/** Given a set of ticket IDs, returns the subset that have at least one
 *  attachment on at least one of their messages. Used by the contact
 *  page's "Tickets & activity" accordion to show a paperclip on rows
 *  that carry attachments without having to expand the timeline.
 *
 *  Implementation: one round-trip that pulls `(ticket_id, attachments)`
 *  for every message in the supplied ticket set, then folds into a
 *  Set client-side. Cheap enough for typical contacts (≤30 tickets
 *  → a few hundred rows). Stable Set identity via the sorted-ids
 *  cache key so consumers' useMemos don't churn. */
export function useTicketsHaveAttachments(ticketIds: string[]) {
  const key = ticketIds.slice().sort().join(',');
  return useQuery({
    queryKey: ['tickets-have-attachments', key],
    enabled: ticketIds.length > 0,
    queryFn: async (): Promise<Set<string>> => {
      if (ticketIds.length === 0) return new Set();
      // 100 ids per request: they all travel in the URL, and a few hundred
      // UUIDs is longer than the gateway accepts.
      const out = new Set<string>();
      const parts = await Promise.all(
        chunk(ticketIds, 100).map((ids) =>
          pageAll<{ ticket_id: string; attachments: unknown }>((from, to) =>
            supabase
              .from('ticket_messages')
              .select('ticket_id, attachments')
              .in('ticket_id', ids)
              .not('attachments', 'is', null)
              .order('id')
              .range(from, to),
          ),
        ),
      );
      for (const rows of parts) {
        for (const row of rows) {
          if (Array.isArray(row.attachments) && row.attachments.length > 0) {
            out.add(row.ticket_id);
          }
        }
      }
      return out;
    },
  });
}

/** Which of these ticket ids belong to this inbox? Used to scope My Day's "For
 *  you" notifications to the inbox you're in. Asked directly (in pieces — the ids
 *  travel in the URL) rather than read off a list of tickets, so a notification
 *  about an old ticket is never hidden because the ticket isn't in some page. */
export function useTicketIdsInInbox(ticketIds: string[], inboxId: string | undefined) {
  const key = [...new Set(ticketIds)].sort().join(',');
  return useQuery({
    queryKey: ['ticket-ids-in-inbox', inboxId ?? null, key],
    enabled: !!inboxId && ticketIds.length > 0,
    queryFn: async (): Promise<Set<string>> => {
      const out = new Set<string>();
      const ids = [...new Set(ticketIds)];
      const parts = await Promise.all(
        chunk(ids, 100).map(async (part) => {
          const { data, error } = await supabase
            .from('tickets')
            .select('id')
            .in('id', part)
            .eq('inbox_id', inboxId as string);
          if (error) throw error;
          return (data ?? []) as Array<{ id: string }>;
        }),
      );
      for (const rows of parts) for (const r of rows) out.add(r.id);
      return out;
    },
  });
}

export type ContactEditLogEntry = Tables['contact_edit_log']['Row'];

/** Append-only audit trail for a contact — "who changed who, and what".
 *  Backed by the contacts_cache AFTER UPDATE trigger (migration 039),
 *  so it captures every edit regardless of path. Newest first. */
export function useContactEditLog(email: string | undefined) {
  return useQuery({
    queryKey: ['contact-edit-log', email],
    enabled: !!email,
    queryFn: async (): Promise<ContactEditLogEntry[]> => {
      if (!email) return [];
      const { data, error } = await supabase
        .from('contact_edit_log')
        .select('*')
        .eq('contact_email', email.toLowerCase())
        .order('created_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data ?? []) as ContactEditLogEntry[];
    },
  });
}

export function useContactTickets(email: string | undefined) {
  return useQuery({
    queryKey: ['contact-tickets', email],
    enabled: !!email,
    queryFn: async (): Promise<Ticket[]> => {
      if (!email) return [];
      // pageAll: a sender with more than 1,000 tickets was silently cut at 1,000.
      return pageAll<Ticket>((from, to) =>
        supabase
          .from('tickets')
          .select('*')
          .eq('sender_email', email.toLowerCase())
          .order('created_at', { ascending: false })
          .order('id')
          .range(from, to),
      );
    },
  });
}

// -------------------- Contacts: directory, overview, notes --------------------
//
// The list and the contact page's numbers come from two Postgres functions
// (migration 073): contact_directory and contact_overview. They do the counting
// server-side (no PostgREST 1,000-row cap) and apply the visibility rule — a
// member only sees contacts who emailed an inbox they can access; admins see all.

/** Call a Postgres function that isn't in the generated database.types yet. */
async function callRpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const rpc = (supabase as unknown as {
    rpc: (f: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
  }).rpc;
  const { data, error } = await rpc.call(supabase, fn, args);
  if (error) throw new Error(error.message);
  return data as T;
}

function directoryArgs(f: ContactFilters, offset: number, limit: number): Record<string, unknown> {
  return {
    p_search: f.q.trim() || null,
    p_relationship: f.relationship,
    p_tier: f.tier === 'any' ? null : f.tier,
    p_open_only: f.openOnly,
    p_include_automated: f.automated,
    p_edited_only: f.edited,
    p_inbox_id: f.inbox || null,
    p_sort: f.sort,
    p_limit: limit,
    p_offset: offset,
  };
}

export function useContactDirectory(filters: ContactFilters) {
  return useInfiniteQuery({
    queryKey: [
      'contact-directory',
      filters.q,
      filters.relationship,
      filters.tier,
      filters.openOnly,
      filters.automated,
      filters.edited,
      filters.inbox,
      filters.sort,
    ],
    initialPageParam: 0,
    staleTime: 30_000,
    queryFn: ({ pageParam }): Promise<ContactDirectory> =>
      callRpc<ContactDirectory>('contact_directory', directoryArgs(filters, pageParam * CONTACT_PAGE_SIZE, CONTACT_PAGE_SIZE)),
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, pg) => n + pg.rows.length, 0);
      return last.rows.length > 0 && loaded < last.total ? all.length : undefined;
    },
    // Keep the previous list on screen while a new search / filter loads.
    placeholderData: (prev) => prev,
  });
}

/** Every contact matching the filters, for the CSV download (the function caps a
 *  page at 200, so this walks pages up to CONTACT_CSV_MAX). */
export async function fetchContactsForCsv(filters: ContactFilters): Promise<ContactDirectoryRow[]> {
  const out: ContactDirectoryRow[] = [];
  const step = 200;
  for (let offset = 0; offset < CONTACT_CSV_MAX; offset += step) {
    const page = await callRpc<ContactDirectory>('contact_directory', directoryArgs(filters, offset, step));
    out.push(...page.rows);
    if (page.rows.length < step || out.length >= page.total) break;
  }
  return out.slice(0, CONTACT_CSV_MAX);
}

/** One contact's numbers (counts, response times, health, 12-month activity…). */
export function useContactOverview(email: string | undefined) {
  return useQuery({
    queryKey: ['contact-overview', email?.toLowerCase()],
    enabled: !!email,
    staleTime: 30_000,
    queryFn: () => callRpc<ContactOverviewData>('contact_overview', { p_email: (email ?? '').toLowerCase() }),
  });
}

/** The newest few tickets from a sender plus their TOTAL count — for the ticket
 *  page's "From this contact" rail, which used to download every ticket (a
 *  1,000+ ticket sender capped out at PostgREST's row limit). */
export function useContactRecentTickets(email: string | undefined, limit = 21) {
  const lower = email?.trim().toLowerCase();
  return useQuery({
    queryKey: ['contact-recent-tickets', lower, limit],
    enabled: !!lower,
    queryFn: async (): Promise<{ rows: Ticket[]; count: number }> => {
      const { data, error, count } = await supabase
        .from('tickets')
        .select('*', { count: 'exact' })
        .eq('sender_email', lower as string)
        .is('merged_into_ticket_id', null)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return { rows: (data ?? []) as Ticket[], count: count ?? 0 };
    },
  });
}

export type ContactTicketStatusFilter = 'all' | 'open' | 'resolved' | 'dismissed';
export const CONTACT_TICKETS_PAGE = 25;
export type ContactTicketsPage = { rows: Ticket[]; count: number };

/** A contact's tickets, 25 at a time (the old hook fetched them all, which a
 *  1,066-ticket sender silently truncated at 1,000). Merged-away husks excluded. */
export function useContactTicketsPage(
  email: string | undefined,
  opts: { status: ContactTicketStatusFilter; inboxId: string; q: string },
) {
  const lower = email?.toLowerCase();
  const term = contactTicketSubjectTerm(opts.q);
  return useInfiniteQuery({
    queryKey: ['contact-tickets-page', lower, opts.status, opts.inboxId, term],
    enabled: !!lower,
    initialPageParam: 0,
    placeholderData: (prev) => prev,
    queryFn: async ({ pageParam }): Promise<ContactTicketsPage> => {
      const from = pageParam * CONTACT_TICKETS_PAGE;
      let q = supabase
        .from('tickets')
        .select('*', { count: 'exact' })
        .eq('sender_email', lower as string)
        .is('merged_into_ticket_id', null)
        .order('created_at', { ascending: false })
        .range(from, from + CONTACT_TICKETS_PAGE - 1);
      if (opts.status === 'open') q = q.in('status', ['new', 'in_progress', 'waiting_on_client']).is('archived_at', null);
      else if (opts.status === 'resolved') q = q.eq('status', 'resolved');
      else if (opts.status === 'dismissed') q = q.eq('status', 'closed');
      if (opts.inboxId) q = q.eq('inbox_id', opts.inboxId);
      if (term) q = q.ilike('subject', `%${term}%`);
      const { data, error, count } = await q;
      if (error) throw error;
      return { rows: (data ?? []) as Ticket[], count: count ?? 0 };
    },
    getNextPageParam: (last: ContactTicketsPage, all: ContactTicketsPage[]) => {
      const loaded = all.reduce((n, pg) => n + pg.rows.length, 0);
      return last.rows.length > 0 && loaded < last.count ? all.length : undefined;
    },
  });
}

/** Server-side contact search for the ⌘K palette (the old one filtered only the
 *  200 most recently synced contacts in the browser). RLS keeps it to contacts
 *  the viewer may see. */
export function useContactSearch(query: string) {
  const term = useDebounce(query.trim(), 200);
  return useQuery({
    queryKey: ['contact-search', term],
    enabled: term.length >= 2,
    staleTime: 30_000,
    queryFn: async (): Promise<Contact[]> => {
      const clause = contactSearchClause(term);
      if (!clause) return [];
      const { data, error } = await supabase
        .from('contacts_cache')
        .select('*')
        .or(clause)
        .eq('is_automated', false)
        .order('full_name', { ascending: true, nullsFirst: false })
        .limit(8);
      if (error) throw error;
      return (data ?? []) as Contact[];
    },
  });
}

// ---- notes feed ----
export type ContactNote = {
  id: string;
  contact_email: string;
  author_slack_id: string;
  body: string;
  pinned: boolean;
  created_at: string;
  updated_at: string;
};

// contact_notes isn't in the generated types yet.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const contactNotesTable = () => (supabase as any).from('contact_notes');

export function useContactNotes(email: string | undefined) {
  const lower = email?.toLowerCase();
  return useQuery({
    queryKey: ['contact-notes', lower],
    enabled: !!lower,
    refetchInterval: 60_000,
    queryFn: async (): Promise<ContactNote[]> => {
      const { data, error } = await contactNotesTable()
        .select('*')
        .eq('contact_email', lower)
        .order('pinned', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as ContactNote[];
    },
  });
}

export function useAddContactNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { email: string; authorSlackId: string; body: string; pinned?: boolean }) => {
      const { data, error } = await contactNotesTable()
        .insert({ contact_email: a.email.toLowerCase(), author_slack_id: a.authorSlackId, body: a.body.trim(), pinned: a.pinned ?? false })
        .select()
        .single();
      if (error) throw error;
      return data as ContactNote;
    },
    onSuccess: (row) => qc.invalidateQueries({ queryKey: ['contact-notes', row.contact_email] }),
  });
}

export function useUpdateContactNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { id: string; email: string; body?: string; pinned?: boolean }) => {
      const patch: Record<string, unknown> = {};
      if (a.body !== undefined) patch.body = a.body.trim();
      if (a.pinned !== undefined) patch.pinned = a.pinned;
      const { data, error } = await contactNotesTable().update(patch).eq('id', a.id).select().maybeSingle();
      if (error) throw error;
      // RLS hides rows you may not edit (someone else's note) — it matches nothing, silently.
      if (!data) throw new Error("You can only edit your own notes (admins can edit any).");
      return data as ContactNote;
    },
    onSuccess: (_row, vars) => qc.invalidateQueries({ queryKey: ['contact-notes', vars.email.toLowerCase()] }),
  });
}

export function useDeleteContactNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { id: string; email: string }) => {
      const { data, error } = await contactNotesTable().delete().eq('id', a.id).select('id');
      if (error) throw error;
      if (!data || data.length === 0) throw new Error("You can only delete your own notes (admins can delete any).");
    },
    onSuccess: (_v, vars) => qc.invalidateQueries({ queryKey: ['contact-notes', vars.email.toLowerCase()] }),
  });
}

// ---- flags on the contact row ----
function invalidateContact(qc: QueryClient, email: string) {
  qc.invalidateQueries({ queryKey: ['contacts'] });
  qc.invalidateQueries({ queryKey: ['contact', email] });
  qc.invalidateQueries({ queryKey: ['contact-directory'] });
  qc.invalidateQueries({ queryKey: ['contact-edit-log', email] });
  qc.invalidateQueries({ queryKey: ['contact-search'] });
}

/** Flag / unflag a sender as automated. Writes ONLY the automated columns, so it
 *  never pins the row against the Patients on Demand sync. */
export function useSetContactAutomated() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { email: string; automated: boolean; editorEmail: string | null }) => {
      const lower = a.email.toLowerCase();
      const patch = { is_automated: a.automated, automated_by: a.editorEmail, automated_at: new Date().toISOString() };
      const { data, error } = await supabase.from('contacts_cache').update(patch).eq('email', lower).select().maybeSingle();
      if (error) throw error;
      if (data) return data as Contact;
      // No cache row yet (rare): create a minimal one.
      const ins = await supabase
        .from('contacts_cache')
        .insert({ email: lower, is_existing_client: false, ...patch })
        .select()
        .single();
      if (ins.error) throw ins.error;
      return ins.data as Contact;
    },
    onSuccess: (row) => {
      invalidateContact(qc, row.email);
      // Flagging moves this sender's tickets between the FYI lane and the working
      // tabs, so everything that lists tickets (and the badges) is refreshed.
      invalidateTicketLists(qc);
      qc.invalidateQueries({ queryKey: ['ticket'] });
      qc.invalidateQueries({ queryKey: ['automated-suggestions'] });
      qc.invalidateQueries({ queryKey: ['fyi-count'] });
      qc.invalidateQueries({ queryKey: ['inbox-new-counts'] });
    },
  });
}

// ── Robot mail: the FYI lane (migration 080) ────────────────────────────────

export interface AutomatedSuggestion {
  email: string;
  name: string | null;
  ticket_count: number;
  last_subject: string | null;
  last_at: string | null;
}

/** Senders that look automated (many tickets, never a reply) but aren't flagged
 *  yet. Nothing is flagged on its own: a person moves each one into the lane. */
export function useAutomatedSuggestions(inboxId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['automated-suggestions', inboxId],
    enabled: !!inboxId && enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<AutomatedSuggestion[]> => {
      const { data, error } = await supabase.rpc('suggest_automated_senders', { p_inbox_id: inboxId as string });
      if (error) throw error;
      return (data ?? []) as AutomatedSuggestion[];
    },
  });
}

/** Open robot-mail tickets waiting in each inbox's FYI lane (My Day's FYI card).
 *  One exact COUNT per inbox, like useInboxNewCounts. */
export function useFyiCounts(inboxIds: string[]) {
  const key = inboxIds.join(',');
  return useQuery({
    queryKey: ['fyi-count', key],
    enabled: inboxIds.length > 0,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<Map<string, number>> => {
      const nowIso = new Date().toISOString();
      const counted = await Promise.all(
        inboxIds.map(async (id) => {
          const { count, error } = await supabase
            .from('tickets')
            .select('id', { count: 'exact', head: true })
            .eq('inbox_id', id)
            .eq('is_automated_sender', true)
            .not('status', 'in', '(resolved,closed)')
            .is('archived_at', null)
            .or(`snoozed_until.is.null,snoozed_until.lte.${nowIso}`);
          if (error) throw error;
          return [id, count ?? 0] as const;
        }),
      );
      const m = new Map<string, number>();
      for (const [id, n] of counted) if (n > 0) m.set(id, n);
      return m;
    },
  });
}

/** Un-pin a hand-edited contact so Patients on Demand updates it again. The row's
 *  fetched_at is reset so the very next inbound email refreshes it. */
export function useResumePodSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { email: string; editorEmail: string | null }) => {
      const { data, error } = await supabase
        .from('contacts_cache')
        .update({ is_manually_edited: false, edited_by: a.editorEmail, edited_at: new Date().toISOString(), fetched_at: '1970-01-01T00:00:00Z' })
        .eq('email', a.email.toLowerCase())
        .select()
        .single();
      if (error) throw error;
      return data as Contact;
    },
    onSuccess: (row) => invalidateContact(qc, row.email),
  });
}

/** The contact's most recent journeys (light columns only). */
export function useContactJourneys(email: string | undefined) {
  const lower = email?.toLowerCase();
  return useQuery({
    queryKey: ['contact-journeys', lower],
    enabled: !!lower,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_journeys')
        .select('id, exported_at, exported_by, trigger, ticket_count')
        .eq('client_email', lower as string)
        .order('exported_at', { ascending: false })
        .limit(3);
      if (error) throw error;
      return (data ?? []) as Array<{ id: string; exported_at: string; exported_by: string; trigger: string; ticket_count: number }>;
    },
  });
}

/** A ticket_event with the foreign-keyed ticket minimally embedded so a
 *  cross-ticket feed can render the source ticket subject + a link
 *  without N+1 lookups. PostgREST returns the embedded relation under
 *  the table name; we pin the shape we actually use here. */
export interface TicketEventWithTicket extends TicketEvent {
  tickets: {
    id: string;
    subject: string | null;
    priority: string | null;
    status: string | null;
    inbox_id: string | null;
  } | null;
}

export interface AllActivityFilters {
  eventTypes?: string[];
  actorSlackIds?: string[];
  /** ISO 8601, inclusive lower bound (created_at >=). */
  from?: string;
  /** ISO 8601, inclusive upper bound (created_at <=). */
  to?: string;
  /** Page number (0-indexed). Used with `pageSize` for "load more". */
  page?: number;
  pageSize?: number;
}

/** Cross-ticket activity feed for the global /activity page. Returns the
 *  most recent ticket_events with the source ticket embedded for the
 *  link. RLS already allows team members to read ticket_events (migration
 *  011), so this is a plain SELECT — no RPC needed. Paginated via
 *  `(page, pageSize)` so a fresh team can scroll back into history
 *  without pulling tens of thousands of rows at once. */
export function useAllTicketEvents(filters: AllActivityFilters = {}) {
  const pageSize = filters.pageSize ?? 50;
  const page = filters.page ?? 0;
  const eventTypesKey = (filters.eventTypes ?? []).slice().sort().join(',');
  const actorsKey = (filters.actorSlackIds ?? []).slice().sort().join(',');
  return useQuery({
    queryKey: [
      'all-ticket-events',
      eventTypesKey,
      actorsKey,
      filters.from ?? '',
      filters.to ?? '',
      page,
      pageSize,
    ],
    queryFn: async (): Promise<TicketEventWithTicket[]> => {
      let q = supabase
        .from('ticket_events')
        .select('*, tickets(id, subject, priority, status, inbox_id)')
        .order('created_at', { ascending: false })
        .range(page * pageSize, page * pageSize + pageSize - 1);

      if (filters.eventTypes && filters.eventTypes.length > 0) {
        q = q.in('event_type', filters.eventTypes);
      }
      if (filters.actorSlackIds && filters.actorSlackIds.length > 0) {
        q = q.in('actor', filters.actorSlackIds);
      }
      if (filters.from) q = q.gte('created_at', filters.from);
      if (filters.to) q = q.lte('created_at', filters.to);

      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as TicketEventWithTicket[];
    },
    // Stable for the duration of the query key; refetched naturally via
    // window-focus polling on the page (see useEffect there). No
    // refetchInterval — this is a "what just happened" feed, not a
    // dashboard widget, so we leave manual control with the page.
  });
}

/** Recent activity (ticket_events) across multiple tickets — used on the
 *  per-contact page to render a single chronological feed spanning every
 *  ticket this contact has ever opened. Capped at 100 most-recent events
 *  for predictable cost; this is "what's happened lately with this
 *  person", not a full audit dump.
 *
 *  Key is the sorted joined ticket id list so the query cache keys
 *  deterministically — different contacts get different cache entries
 *  even though they share this hook. */
export function useContactActivity(ticketIds: string[]) {
  const key = ticketIds.slice().sort().join(',');
  return useQuery({
    queryKey: ['contact-activity', key],
    enabled: ticketIds.length > 0,
    queryFn: async (): Promise<TicketEvent[]> => {
      if (ticketIds.length === 0) return [];
      const { data, error } = await supabase
        .from('ticket_events')
        .select('*')
        .in('ticket_id', ticketIds)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as TicketEvent[];
    },
  });
}

// -------------------- Client journeys --------------------
//
// The list reads only the cheap columns (+ two payload fields for the name and
// sentiment) — never the full payload, which holds every message body. The full
// row is fetched on demand by useClientJourney (detail dialog / print page).
// Filter / sort / search logic lives in lib/journeys-filters.ts (unit-tested).

/** Columns the list needs. `client_name` and `sentiment_series` are pulled out
 *  of the jsonb payload server-side. */
const JOURNEY_LIST_COLUMNS =
  'id, client_email, exported_at, exported_by, trigger, narrative_summary, ticket_count, date_range_start, date_range_end, client_name:payload->>client_name, sentiment_series:payload->sentiment_series';

export type JourneyListRow = Pick<
  ClientJourney,
  | 'id'
  | 'client_email'
  | 'exported_at'
  | 'exported_by'
  | 'trigger'
  | 'narrative_summary'
  | 'ticket_count'
  | 'date_range_start'
  | 'date_range_end'
> & {
  client_name: string | null;
  sentiment_series: Array<{ score: number }> | null;
};

// The builder type is unwieldy to thread through; this only chains
// .or/.eq/.gte/.order on whatever from().select() returned.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function applyJourneyFilters(q: any, f: JourneyFilters) {
  const or = searchOrClause(f.q);
  if (or) q = q.or(or);
  if (f.trigger !== 'all') q = q.eq('trigger', f.trigger);
  const since = periodStart(f.period, Date.now());
  if (since) q = q.gte('exported_at', since);
  const { column, ascending } = sortSpec(f.sort);
  q = q.order(column, { ascending });
  // Stable tie-break so "Load more" never repeats or skips a row.
  if (column !== 'exported_at') q = q.order('exported_at', { ascending: false });
  return q.order('id', { ascending: true });
}

export function useJourneyList(filters: JourneyFilters) {
  return useInfiniteQuery({
    queryKey: ['client-journeys', 'list', filters.q, filters.trigger, filters.period, filters.sort],
    initialPageParam: 0,
    queryFn: async ({ pageParam }): Promise<{ rows: JourneyListRow[]; count: number }> => {
      const from = pageParam * JOURNEY_PAGE_SIZE;
      const q = applyJourneyFilters(
        supabase.from('client_journeys').select(JOURNEY_LIST_COLUMNS, { count: 'exact' }),
        filters,
      ).range(from, from + JOURNEY_PAGE_SIZE - 1);
      const { data, error, count } = await q;
      if (error) throw error;
      return { rows: (data ?? []) as unknown as JourneyListRow[], count: count ?? 0 };
    },
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + p.rows.length, 0);
      return last.rows.length > 0 && loaded < last.count ? all.length : undefined;
    },
    // Keep the previous results on screen while a new search/filter loads.
    placeholderData: (prev) => prev,
  });
}

/** One full journey (with payload) for the detail dialog + print page. */
export function useClientJourney(id: string | null | undefined) {
  return useQuery({
    queryKey: ['client-journeys', 'one', id],
    enabled: !!id,
    queryFn: async (): Promise<ClientJourney | null> => {
      const { data, error } = await supabase
        .from('client_journeys')
        .select('*')
        .eq('id', id as string)
        .maybeSingle();
      if (error) throw error;
      return (data as ClientJourney | null) ?? null;
    },
  });
}

export interface JourneyStats {
  total: number;
  last30d: number;
  manual: number;
  automatic: number;
}

/** Counts for the stats strip and the trigger tabs (head-only, no rows). */
export function useJourneyStats() {
  return useQuery({
    queryKey: ['client-journeys', 'stats'],
    queryFn: async (): Promise<JourneyStats> => {
      const count = async (build: (q: any) => any) => {
        const { count: n, error } = await build(
          supabase.from('client_journeys').select('id', { count: 'exact', head: true }),
        );
        if (error) throw error;
        return (n as number | null) ?? 0;
      };
      const since = periodStart('30d', Date.now()) as string;
      const [total, last30d, manual, automatic] = await Promise.all([
        count((q) => q),
        count((q) => q.gte('exported_at', since)),
        count((q) => q.eq('trigger', 'manual')),
        count((q) => q.eq('trigger', 'auto_30d_inactivity')),
      ]);
      return { total, last30d, manual, automatic };
    },
    staleTime: 30_000,
  });
}

/** Every journey matching the filters (no paging, list columns only) for the
 *  CSV download. PostgREST caps a response at 1000 rows, so this walks pages up
 *  to JOURNEY_CSV_MAX. */
export async function fetchJourneysForCsv(filters: JourneyFilters): Promise<JourneyListRow[]> {
  const out: JourneyListRow[] = [];
  const step = 1000;
  for (let from = 0; from < JOURNEY_CSV_MAX; from += step) {
    const { data, error } = await applyJourneyFilters(
      supabase.from('client_journeys').select(JOURNEY_LIST_COLUMNS),
      filters,
    ).range(from, from + step - 1);
    if (error) throw error;
    const rows = (data ?? []) as unknown as JourneyListRow[];
    out.push(...rows);
    if (rows.length < step) break;
  }
  return out.slice(0, JOURNEY_CSV_MAX);
}

// -------------------- Reports --------------------
//
// One call to the report_metrics Postgres function (migration 072) returns every
// number on the Reports page: KPIs (current + previous period), the time series,
// distributions, breakdowns, team table, heatmap, top clients and the live
// backlog. Sydney-day buckets, exact counts, no PostgREST row cap. The access
// check (active team member + inbox access) happens inside the function.

export interface ReportMetricsArgs {
  fromIso: string;
  toIso: string;
  prevFromIso: string;
  bucket: ReportBucket;
  inboxIds: string[];
  /** Slack ids (already expanded from any selected teams). */
  assignees: string[];
  unassigned: boolean;
  priorities: string[];
  categories: string[];
}

export function useReportMetrics(a: ReportMetricsArgs) {
  const key = [
    'report-metrics',
    a.fromIso,
    a.toIso,
    a.prevFromIso,
    a.bucket,
    [...a.inboxIds].sort().join(','),
    [...a.assignees].sort().join(','),
    a.unassigned,
    [...a.priorities].sort().join(','),
    [...a.categories].sort().join(','),
  ];
  return useQuery({
    queryKey: key,
    staleTime: 60_000,
    // Keep the old numbers on screen while a new filter loads (no blank flash).
    placeholderData: (prev) => prev,
    queryFn: async (): Promise<ReportMetrics> => {
      // report_metrics isn't in the generated database.types yet.
      const rpc = (supabase as unknown as {
        rpc: (fn: string, args: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
      }).rpc;
      const { data, error } = await rpc.call(supabase, 'report_metrics', {
        p_from: a.fromIso,
        p_to: a.toIso,
        p_inbox_ids: a.inboxIds.length ? a.inboxIds : null,
        p_assignees: a.assignees.length ? a.assignees : null,
        p_unassigned: a.unassigned,
        p_priorities: a.priorities.length ? a.priorities : null,
        p_categories: a.categories.length ? a.categories : null,
        p_bucket: a.bucket,
        p_prev_from: a.prevFromIso,
      });
      if (error) throw new Error(error.message);
      return data as ReportMetrics;
    },
  });
}

// -------------------- KB --------------------

export function useKbArticles() {
  return useQuery({
    queryKey: ['kb-articles'],
    queryFn: async (): Promise<KbArticle[]> => {
      const { data, error } = await supabase
        .from('kb_articles')
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as KbArticle[];
    },
  });
}

export function useUpsertKbArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (article: Partial<KbArticle> & { title: string; content: string }) => {
      const { data, error } = await supabase
        .from('kb_articles')
        .upsert(article)
        .select()
        .single();
      if (error) throw error;
      return data as KbArticle;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kb-articles'] });
    },
  });
}

export function useDeleteKbArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('kb_articles').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kb-articles'] });
    },
  });
}

// -------------------- SLA Rules --------------------

export function useSlaRules() {
  return useQuery({
    queryKey: ['sla-rules'],
    queryFn: async (): Promise<SlaRule[]> => {
      const { data, error } = await supabase
        .from('sla_rules')
        .select('*')
        .order('priority', { ascending: true })
        .order('client_tier', { ascending: true });
      if (error) throw error;
      return (data ?? []) as SlaRule[];
    },
  });
}

export function useUpsertSlaRules() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rules: Partial<SlaRule>[]) => {
      const { data, error } = await supabase.from('sla_rules').upsert(rules).select();
      if (error) throw error;
      return data as SlaRule[];
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sla-rules'] });
    },
  });
}

// -------------------- Team --------------------

export function useTeamMembers() {
  return useQuery({
    queryKey: ['team-members'],
    queryFn: async (): Promise<TeamMember[]> => {
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .order('email', { ascending: true });
      if (error) throw error;
      return (data ?? []) as TeamMember[];
    },
  });
}

// -------------------- Notifications --------------------
//
// RLS scopes SELECT/UPDATE to rows where recipient_slack_id matches the
// signed-in user's team_members.slack_user_id, so the queries don't need
// an explicit recipient filter. The SPA never INSERTs/DELETEs — workflows do.
//
// Freshness: Supabase Realtime (migration 054 + `useNotificationsRealtime`)
// invalidates these keys within ~200ms of a row change; the 25s poll below is
// the backstop for a silently dropped WebSocket.
//
// Cache layout — everything except the head count sits under the
// `['notifications']` prefix, so one prefix invalidation covers all of it:
//   ['notifications', 'list', { limit, tab, unreadOnly }]  one entry per view
//   ['notifications', 'unread-types']                       { type: n } for unread rows
//   ['notifications-unread-count']                          exact head count (bell badge,
//                                                           tab title, favicon)

// Exported so `useNotificationsRealtime` can invalidate the same keys
// the bell + unread badge depend on, without re-declaring them.
export const NOTIFICATIONS_KEY = ['notifications'] as const;
export const NOTIFICATIONS_LIST_KEY = ['notifications', 'list'] as const;
export const NOTIFICATIONS_UNREAD_TYPES_KEY = ['notifications', 'unread-types'] as const;
export const NOTIFICATIONS_UNREAD_COUNT_KEY = ['notifications-unread-count'] as const;
/** Shared by every notification write so `isMutating` can see them all. */
export const NOTIFICATIONS_MUTATION_KEY = ['notifications', 'write'] as const;

export function notificationsListKey(
  limit: number,
  tab: NotificationTabId = 'all',
  unreadOnly = false,
) {
  return [...NOTIFICATIONS_LIST_KEY, { limit, tab, unreadOnly }] as const;
}

/** Refetch every notification query (lists, per-type counts, head count). */
export function invalidateNotifications(qc: QueryClient) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY }),
    qc.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_COUNT_KEY }),
  ]);
}

interface UseNotificationsOptions {
  /** Server-side type filter. Default 'all' = no filter. */
  tab?: NotificationTabId;
  /** Server-side `read_at is null` filter. */
  unreadOnly?: boolean;
}

export function useNotifications(limit = 30, opts: UseNotificationsOptions = {}) {
  const { tab = 'all', unreadOnly = false } = opts;
  const qc = useQueryClient();
  // Generics spelled out: with a zero-argument `placeholderData` function,
  // inference otherwise takes the data type to be the function itself.
  return useQuery<Notification[], Error>({
    queryKey: notificationsListKey(limit, tab, unreadOnly),
    queryFn: async ({ signal }) => {
      // Filter on the server so a tab shows the latest N *of its category*,
      // not whichever few of the latest N overall happen to match. Filters
      // first, then order/limit — the builder's filter methods live on the
      // object `.select()` returns.
      let q = supabase.from('notifications').select('*');
      const types = typesForTab(tab);
      if (types) q = q.in('type', [...types]);
      if (unreadOnly) q = q.is('read_at', null);
      const { data, error } = await q
        .order('created_at', { ascending: false })
        .limit(limit)
        .abortSignal(signal);
      if (error) throw error;
      return (data ?? []) as Notification[];
    },
    // While a tab's own query is loading, show what we already hold: the same
    // tab without the unread filter, else the All list (the bell keeps that
    // one warm). Filtered client-side so the placeholder never shows a row the
    // real result would exclude. Deliberately NOT `keepPreviousData`, which
    // would show the previous tab's rows under the new tab's label.
    placeholderData: () => {
      for (const source of [
        notificationsListKey(limit, tab, false),
        notificationsListKey(limit, 'all', false),
      ]) {
        const cached = qc.getQueryData<Notification[]>(source);
        if (cached) return cached.filter((n) => matchesView(n, tab, unreadOnly));
      }
      return undefined;
    },
    // Realtime is the primary freshness mechanism; this shorter poll is the
    // backstop for cases where the WebSocket dropped silently (transient
    // network blip, suspended tab on mobile), so the bell never goes more
    // than ~25s out of sync. refetchOnWindowFocus picks up the slack when the
    // user tabs back to a long-idle SPA.
    refetchInterval: 25_000,
    refetchOnWindowFocus: true,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: NOTIFICATIONS_UNREAD_COUNT_KEY,
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .is('read_at', null);
      if (error) throw error;
      return count ?? 0;
    },
    // See the note on `useNotifications` — realtime first, poll as backstop.
    refetchInterval: 25_000,
    refetchOnWindowFocus: true,
  });
}

/**
 * Unread rows bucketed by `type`, for the per-tab badges. Selects only the
 * `type` column. PostgREST caps a response at 1000 rows, which is far more
 * unread notifications than anyone acts on — the badges cap at "99+" anyway.
 * Only mount this where the tabs are showing (the open bell panel, the page).
 */
export function useUnreadTypeCounts() {
  return useQuery({
    queryKey: NOTIFICATIONS_UNREAD_TYPES_KEY,
    queryFn: async ({ signal }): Promise<UnreadTypeCounts> => {
      const { data, error } = await supabase
        .from('notifications')
        .select('type')
        .is('read_at', null)
        .limit(1000)
        .abortSignal(signal);
      if (error) throw error;
      return countByType(data ?? []);
    },
    refetchInterval: 25_000,
    refetchOnWindowFocus: true,
  });
}

// ---- Notification writes ----
//
// Every write shares NOTIFICATIONS_MUTATION_KEY. Two things depend on that:
//   * `settleNotificationWrites` only refetches once the LAST in-flight write
//     finishes, so triaging several rows quickly doesn't refetch (and flicker)
//     between clicks.
//   * `useNotificationsRealtime` ignores its own echo of an UPDATE while a
//     write is in flight.

function cancelNotificationQueries(qc: QueryClient) {
  return Promise.all([
    qc.cancelQueries({ queryKey: NOTIFICATIONS_KEY }),
    qc.cancelQueries({ queryKey: NOTIFICATIONS_UNREAD_COUNT_KEY }),
  ]);
}

/**
 * onSettled body shared by every notification write. In TanStack Query v5 the
 * mutation is still pending while its own `onSettled` runs, so `> 1` means
 * "another write is still in flight — it will refetch when it settles".
 */
function settleNotificationWrites(qc: QueryClient) {
  if (qc.isMutating({ mutationKey: NOTIFICATIONS_MUTATION_KEY }) > 1) return;
  return invalidateNotifications(qc);
}

function patchNotificationLists(
  qc: QueryClient,
  updater: (rows: Notification[]) => Notification[],
) {
  qc.setQueriesData<Notification[]>({ queryKey: NOTIFICATIONS_LIST_KEY }, (old) =>
    old ? updater(old) : old,
  );
}

/** Adjust the head count + per-type counts. Leaves an unloaded cache alone. */
function bumpUnreadCounts(qc: QueryClient, type: string, delta: number) {
  qc.setQueryData<number>(NOTIFICATIONS_UNREAD_COUNT_KEY, (c) =>
    c === undefined ? c : Math.max(0, c + delta),
  );
  qc.setQueryData<UnreadTypeCounts>(NOTIFICATIONS_UNREAD_TYPES_KEY, (m) =>
    m ? bumpTypeCount(m, type, delta) : m,
  );
}

function findCachedNotification(qc: QueryClient, id: string): Notification | undefined {
  for (const [, rows] of qc.getQueriesData<Notification[]>({ queryKey: NOTIFICATIONS_LIST_KEY })) {
    const hit = rows?.find((n) => n.id === id);
    if (hit) return hit;
  }
  return undefined;
}

interface SetReadVars {
  id: string;
  /** Row's notification type — needed to keep the per-type counts in step. */
  type: string;
  /** true = mark read, false = mark unread. */
  read: boolean;
}

interface SetReadContext {
  /** `undefined` = the row wasn't in any cache, so nothing was patched. */
  prevReadAt: string | null | undefined;
  /** Unread-count change applied optimistically: -1, 0 or +1. */
  delta: number;
}

/**
 * Flip one notification between read and unread — optimistic, so the badge,
 * tab counts, tab title and favicon all move on click, not after a round trip.
 *
 * The row is patched IN PLACE in every cached list rather than removed from
 * the lists it no longer belongs to (an "Unread only" view): the view keeps it
 * on screen until the user changes view (see `use-notifications-view.ts`), and
 * the refetch on settle then reconciles with the server.
 */
export function useSetNotificationRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, SetReadVars, SetReadContext>({
    mutationKey: NOTIFICATIONS_MUTATION_KEY,
    mutationFn: async ({ id, read }) => {
      const base = supabase
        .from('notifications')
        .update({ read_at: read ? new Date().toISOString() : null })
        .eq('id', id);
      // Guard on the current state so a no-op writes nothing and therefore
      // emits no realtime UPDATE event.
      const { error } = await (read ? base.is('read_at', null) : base.not('read_at', 'is', null));
      if (error) throw error;
    },
    onMutate: async ({ id, type, read }) => {
      // Abort in-flight fetches so a response that started before this click
      // can't land afterwards and undo the patch below.
      await cancelNotificationQueries(qc);

      const cached = findCachedNotification(qc, id);
      const prevReadAt = cached ? cached.read_at : undefined;
      let delta = 0;
      if (cached && (cached.read_at !== null) !== read) delta = read ? -1 : 1;

      const readAt = read ? new Date().toISOString() : null;
      patchNotificationLists(qc, (rows) => applyReadState(rows, id, readAt));
      if (delta !== 0) bumpUnreadCounts(qc, type, delta);
      return { prevReadAt, delta };
    },
    onError: (_err, { id, type }, ctx) => {
      if (!ctx) return;
      // Inverse-patch just this row and the counts. Restoring a whole
      // snapshot instead would wipe a notification that arrived mid-flight.
      if (ctx.prevReadAt !== undefined) {
        patchNotificationLists(qc, (rows) => applyReadState(rows, id, ctx.prevReadAt ?? null));
      }
      if (ctx.delta !== 0) bumpUnreadCounts(qc, type, -ctx.delta);
    },
    onSettled: () => settleNotificationWrites(qc),
  });
}

interface MarkAllVars {
  /** Types to mark read (the active tab), or `null` for everything. */
  types: readonly NotificationType[] | null;
}

/**
 * Mark everything read — or, when `types` is set, only those types (the
 * active tab). RLS scopes the UPDATE to the caller's own rows.
 */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: NOTIFICATIONS_MUTATION_KEY,
    mutationFn: async ({ types }: MarkAllVars) => {
      let q = supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .is('read_at', null);
      if (types) q = q.in('type', [...types]);
      const { error } = await q;
      if (error) throw error;
    },
    onMutate: async ({ types }) => {
      await cancelNotificationQueries(qc);
      const readAt = new Date().toISOString();
      patchNotificationLists(qc, (rows) =>
        applyReadStateWhere(
          rows,
          (n) => types === null || (types as readonly string[]).includes(n.type),
          readAt,
        ),
      );

      if (types === null) {
        qc.setQueryData<number>(NOTIFICATIONS_UNREAD_COUNT_KEY, (c) => (c === undefined ? c : 0));
        qc.setQueryData<UnreadTypeCounts>(NOTIFICATIONS_UNREAD_TYPES_KEY, (m) => (m ? {} : m));
        return;
      }
      // Scoped: only adjust the head count when the per-type counts are
      // loaded to say how many rows this removes; otherwise the refetch on
      // settle sets it.
      const typeCounts = qc.getQueryData<UnreadTypeCounts>(NOTIFICATIONS_UNREAD_TYPES_KEY);
      if (!typeCounts) return;
      const removed = types.reduce((n, t) => n + (typeCounts[t] ?? 0), 0);
      qc.setQueryData<number>(NOTIFICATIONS_UNREAD_COUNT_KEY, (c) =>
        c === undefined ? c : Math.max(0, c - removed),
      );
      qc.setQueryData<UnreadTypeCounts>(NOTIFICATIONS_UNREAD_TYPES_KEY, (m) => {
        if (!m) return m;
        const next = { ...m };
        for (const t of types) next[t] = 0;
        return next;
      });
    },
    // No inverse patch on error: `onSettled` refetches the true state.
    onSettled: () => settleNotificationWrites(qc),
  });
}

// Marks any unread notifications for the given ticket as read.
// Used by the ticket detail route to keep the badge accurate when a user
// navigates straight to a ticket from another surface (Slack, email, etc.).
// Invalidate-only: it fires on ticket mount when nobody may be watching the
// bell, and the cached lists needn't hold every row for that ticket.
export function useMarkTicketNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: NOTIFICATIONS_MUTATION_KEY,
    mutationFn: async (ticketId: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('ticket_id', ticketId)
        .is('read_at', null);
      if (error) throw error;
      return ticketId;
    },
    onSettled: () => settleNotificationWrites(qc),
  });
}

// -------------------- Ticket watchers --------------------
//
// `ticket_watchers` is RLS'd: any active team member can SELECT all rows, but
// can only INSERT/DELETE rows where `slack_user_id` matches their own
// team_members row. The polling cadence (refetch on focus + 30s interval)
// keeps the avatars + Watch button state fresh without realtime.

export type Watcher = Tables['ticket_watchers']['Row'];

export function useTicketWatchers(ticketId?: string) {
  return useQuery({
    queryKey: ['ticket-watchers', ticketId],
    enabled: !!ticketId,
    queryFn: async (): Promise<Watcher[]> => {
      if (!ticketId) return [];
      const { data, error } = await supabase
        .from('ticket_watchers')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('watching_since', { ascending: true });
      if (error) throw error;
      return (data ?? []) as Watcher[];
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });
}

// Returns whether the currently signed-in user is watching this ticket.
// We resolve the current user's slack_user_id via team_members.email.
// Kept as a separate query (rather than derived from useTicketWatchers)
// so the Watch/Unwatch button can render a stable boolean without needing
// to know about the auth context.
export function useIsWatching(ticketId?: string) {
  return useQuery({
    queryKey: ['is-watching', ticketId],
    enabled: !!ticketId,
    queryFn: async (): Promise<boolean> => {
      if (!ticketId) return false;
      const { data: userResp } = await supabase.auth.getUser();
      const email = userResp.user?.email?.toLowerCase();
      if (!email) return false;

      const { data: tm, error: tmErr } = await supabase
        .from('team_members')
        .select('slack_user_id')
        .eq('email', email)
        .eq('active', true)
        .maybeSingle();
      if (tmErr) throw tmErr;
      if (!tm?.slack_user_id) return false;

      const { data, error } = await supabase
        .from('ticket_watchers')
        .select('ticket_id')
        .eq('ticket_id', ticketId)
        .eq('slack_user_id', tm.slack_user_id)
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function useToggleWatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ticketId,
      watching,
      slackUserId,
      actorSlackId,
      actorEmail,
    }: {
      ticketId: string;
      watching: boolean;
      slackUserId: string;
      /** The signed-in user's slack id. When present AND different from
       *  `slackUserId` on a `watching: true` toggle, we ping the
       *  recipient via `notify_watcher_added` so they know who added
       *  them. Self-toggles (slackUserId === actorSlackId) don't
       *  notify — no point telling yourself. */
      actorSlackId?: string | null;
      /** Signed-in user's email — embedded in the notification title
       *  ("<Liz> added you as a watcher"). The RPC also looks up the
       *  matching team_members row to swap email for display name. */
      actorEmail?: string | null;
    }) => {
      if (watching) {
        const { error } = await supabase
          .from('ticket_watchers')
          .insert({ ticket_id: ticketId, slack_user_id: slackUserId });
        if (error) throw error;
        // Notify the freshly-added watcher iff it's someone OTHER than
        // the actor. Done AFTER the insert succeeds so we never tell
        // someone they're watching a ticket when the subscribe actually
        // failed. Errors here are swallowed — the watch itself worked,
        // the notification is best-effort polish.
        if (
          actorSlackId &&
          slackUserId !== actorSlackId &&
          actorEmail &&
          actorEmail.trim().length > 0
        ) {
          const { error: notifyErr } = await supabase.rpc('notify_watcher_added', {
            p_ticket_id: ticketId,
            p_recipient_slack_id: slackUserId,
            p_actor_email: actorEmail,
          });
          if (notifyErr) {
            // Non-fatal — log so we can spot it in dev without breaking
            // the user's interaction.
            console.warn('notify_watcher_added failed:', notifyErr.message);
          }
        }
      } else {
        const { error } = await supabase
          .from('ticket_watchers')
          .delete()
          .eq('ticket_id', ticketId)
          .eq('slack_user_id', slackUserId);
        if (error) throw error;
      }
      return { ticketId, watching };
    },
    onSuccess: ({ ticketId }) => {
      qc.invalidateQueries({ queryKey: ['ticket-watchers', ticketId] });
      qc.invalidateQueries({ queryKey: ['is-watching', ticketId] });
      // The "Watching" lists on My Day + the dashboard tab key off this.
      qc.invalidateQueries({ queryKey: ['my-watched-ticket-ids'] });
    },
  });
}

// -------------------- Away coverage (migration 043) + leave approval (046) ----

/** Narrowed status — CHECK constraint from migration 046. */
export type LeaveStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
/** Narrowed type — CHECK constraint from migration 046. */
export type LeaveType = 'annual' | 'sick' | 'personal' | 'other';

/** team_away_periods row. The `status` + `leave_type` columns come from
 *  migration 046; `decided_by/at/note` are populated by admins when they
 *  approve or reject. NOTE: only `status='approved'` rows trigger
 *  coverage fan-out or "On leave" badges — see migration 046 for the
 *  resolver change. */
export type AwayPeriod = Omit<Tables['team_away_periods']['Row'], 'status' | 'leave_type'> & {
  status: LeaveStatus;
  leave_type: LeaveType;
};

export interface AwayPeriodEdit {
  /** ISO 8601, when the away window starts. */
  starts_at: string;
  /** ISO 8601, when it ends. Must be > starts_at (DB CHECK enforces). */
  ends_at: string;
  /** Slack id of the teammate covering. Must differ from
   *  `team_member_slack_id` (DB CHECK enforces). */
  backup_slack_id: string;
  /** Optional free-text label ("vacation", "conference"). */
  reason?: string | null;
  /** Leave category (annual/sick/personal/other). Defaults to 'annual'
   *  if omitted — see migration 046 default. */
  leave_type?: LeaveType;
}

/** A trimmed ticket_event row (actor + type + time only) for cheap
 *  team-wide aggregation in the leave recap. We don't need the body /
 *  payload / ticket here — just enough to tally per-person + overall. */
export interface ActivityTally {
  actor: string;
  event_type: string;
  created_at: string;
}

/** TEAM-WIDE activity in a window — every ticket_event across all
 *  tickets in [from, to]. Powers the "Across the team while you were
 *  away" section of the leave recap. Capped at 2000 rows (plenty for a
 *  couple-week window at this team's volume); aggregation into
 *  per-teammate + overall counts happens client-side. Selects only the
 *  three columns the tally needs to keep the payload small. */
export function useTeamActivityInWindow(
  from: string | null | undefined,
  to: string | null | undefined,
) {
  return useQuery({
    queryKey: ['team-activity-window', from ?? null, to ?? null],
    enabled: !!from && !!to,
    queryFn: async (): Promise<ActivityTally[]> => {
      if (!from || !to) return [];
      const { data, error } = await supabase
        .from('ticket_events')
        .select('actor, event_type, created_at')
        .gte('created_at', from)
        .lte('created_at', to)
        .order('created_at', { ascending: false })
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as ActivityTally[];
    },
  });
}

/** Internal notes authored across the team in a window. Notes live in
 *  `ticket_notes` (not `ticket_events`), so the leave recap needs a
 *  second query to fold "N notes" into each teammate's tally. Returns
 *  just author + timestamp; the @-mention fan-out is already counted via
 *  notifications elsewhere, so we tally authored notes here, not
 *  mentions received. RLS allows team-wide SELECT (migration 018). */
export function useTeamNotesInWindow(
  from: string | null | undefined,
  to: string | null | undefined,
) {
  return useQuery({
    queryKey: ['team-notes-window', from ?? null, to ?? null],
    enabled: !!from && !!to,
    queryFn: async (): Promise<Array<{ author_slack_id: string; created_at: string }>> => {
      if (!from || !to) return [];
      const { data, error } = await supabase
        .from('ticket_notes')
        .select('author_slack_id, created_at')
        .gte('created_at', from)
        .lte('created_at', to)
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as Array<{ author_slack_id: string; created_at: string }>;
    },
  });
}

/** Activity that happened on the returning user's tickets during a
 *  past away window — powers the "Welcome back" recap on My Day.
 *  Returns ticket_events in [from, to] for tickets CURRENTLY assigned
 *  to `slackId`, with the source ticket embedded for the link.
 *
 *  PostgREST `tickets!inner(...)` + `tickets.assignee_slack_id=eq.` is
 *  an inner-join filter — only events whose parent ticket is currently
 *  yours come back. Scope is deliberately "my tickets" (the most useful
 *  catch-up surface); coverage on tickets that were reassigned away
 *  mid-leave is out of scope for v1. */
export function useLeaveRecapActivity(
  slackId: string | null | undefined,
  from: string | null | undefined,
  to: string | null | undefined,
) {
  return useQuery({
    queryKey: ['leave-recap-activity', slackId ?? null, from ?? null, to ?? null],
    enabled: !!slackId && !!from && !!to,
    queryFn: async (): Promise<TicketEventWithTicket[]> => {
      if (!slackId || !from || !to) return [];
      const { data, error } = await supabase
        .from('ticket_events')
        .select('*, tickets!inner(id, subject, priority, status, assignee_slack_id)')
        .gte('created_at', from)
        .lte('created_at', to)
        .eq('tickets.assignee_slack_id', slackId)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as unknown as TicketEventWithTicket[];
    },
  });
}

/** All CURRENTLY-ACTIVE away periods, team-wide. Used by the
 *  ticket-detail banner + the TicketRow avatar badge to call out
 *  "Rex is on leave, Liz covers until …" without N+1 lookups.
 *
 *  Returns a Map keyed by `team_member_slack_id` (the away person).
 *  At most one active row per person in practice — schedules are
 *  open-ended but should not overlap for the same person; if they
 *  somehow do, last-write wins in the map (acceptable, rare). */
export function useActiveAwayPeriods() {
  return useQuery({
    queryKey: ['active-away-periods'],
    queryFn: async (): Promise<Map<string, AwayPeriod>> => {
      const nowIso = new Date().toISOString();
      // Migration 046: only approved leave drives "On leave" UI + coverage
      // fan-out. Pending requests don't render anything until decided.
      const { data, error } = await supabase
        .from('team_away_periods')
        .select('*')
        .eq('status', 'approved')
        .lte('starts_at', nowIso)
        .gt('ends_at', nowIso);
      if (error) throw error;
      const m = new Map<string, AwayPeriod>();
      for (const row of (data ?? []) as AwayPeriod[]) {
        m.set(row.team_member_slack_id, row);
      }
      return m;
    },
    // Refetch every 5 min so the badge appears / clears within ~5
    // minutes of a window opening or closing without users having
    // to hit reload. Cheap query.
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Every away period for ONE team member, ordered so future windows
 *  come first then the active one, then past (descending by start).
 *  Used by the Settings → Away coverage section. Reads are RLS-gated
 *  team-wide, so any active team member can read anyone's; this is
 *  scoped to the signed-in user by the caller passing their own
 *  slack id. */
export function useMyAwayPeriods(slackUserId: string | null | undefined) {
  return useQuery({
    queryKey: ['my-away-periods', slackUserId ?? null],
    enabled: !!slackUserId,
    queryFn: async (): Promise<AwayPeriod[]> => {
      if (!slackUserId) return [];
      const { data, error } = await supabase
        .from('team_away_periods')
        .select('*')
        .eq('team_member_slack_id', slackUserId)
        .order('starts_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as AwayPeriod[];
    },
  });
}

/** Submit a NEW leave request (migration 046) OR edit an existing
 *  pending one. Member-scoped — RLS only lets you write rows where
 *  `team_member_slack_id` matches the signed-in user's slack id, and
 *  the status guard trigger blocks any attempt to insert/promote
 *  to anything other than `pending` (admins use `useDecideLeaveRequest`
 *  for that path).
 *
 *  After a successful INSERT, also fires the admin in-app notification
 *  fan-out via the `notify_admins_leave_requested` RPC. The Slack DM
 *  fan-out happens via workflow 16 — call `postLeaveNotify` from the
 *  component after this mutation resolves (we can't import api.ts from
 *  here without a cycle). */
export function useUpsertAwayPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id?: string;
      team_member_slack_id: string;
      patch: AwayPeriodEdit;
    }) => {
      const isInsert = !args.id;
      const row = {
        ...(args.id ? { id: args.id } : {}),
        team_member_slack_id: args.team_member_slack_id,
        ...args.patch,
        // Explicit on every write so the column default ('approved' for
        // bootstrap compat) can't accidentally short-circuit approval.
        // Editing a pending row preserves pending; new inserts start
        // pending. Admins use `useDecideLeaveRequest` to move forward.
        status: 'pending' as LeaveStatus,
      };
      const { data, error } = await supabase
        .from('team_away_periods')
        .upsert(row, { onConflict: 'id' })
        .select()
        .single();
      if (error) throw error;
      const created = data as AwayPeriod;

      // Best-effort in-app fan-out to admins on NEW requests. Don't fail
      // the submit if this RPC errors — the row is already persisted and
      // admins can see it on /leave regardless.
      if (isInsert) {
        try {
          await supabase.rpc('notify_admins_leave_requested', { p_period_id: created.id });
        } catch {
          // Swallow — notification is secondary to the request being recorded.
        }
      }

      return created;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['my-away-periods', row.team_member_slack_id] });
      qc.invalidateQueries({ queryKey: ['all-leave-requests'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useDeleteAwayPeriod() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; team_member_slack_id: string }) => {
      // Member self-withdraw: hard-delete a `pending` row. RLS in 046
      // restricts this — approved rows can't be deleted by the member,
      // they have to ask an admin to cancel (which soft-cancels via
      // status='cancelled' for the audit trail).
      const { error } = await supabase
        .from('team_away_periods')
        .delete()
        .eq('id', args.id);
      if (error) throw error;
      return args;
    },
    onSuccess: (args) => {
      qc.invalidateQueries({ queryKey: ['my-away-periods', args.team_member_slack_id] });
      qc.invalidateQueries({ queryKey: ['all-leave-requests'] });
    },
  });
}

/** Admin queue: every leave request, optionally filtered by status.
 *  Joined with team_members on both `team_member_slack_id` (the
 *  requester) and `backup_slack_id` (the nominated cover) so the table
 *  can show names + avatars without a second lookup. RLS lets every
 *  active team member READ all rows (migration 043), so this works for
 *  agents too — the UI gates the *write* actions behind `is_admin()`. */
export interface LeaveRequestWithNames extends AwayPeriod {
  requester_name: string | null;
  requester_avatar_url: string | null;
  backup_name: string | null;
  backup_avatar_url: string | null;
}

export function useAllLeaveRequests(filter?: LeaveStatus | 'all') {
  const status = filter ?? 'all';
  return useQuery({
    queryKey: ['all-leave-requests', status],
    queryFn: async (): Promise<LeaveRequestWithNames[]> => {
      let q = supabase
        .from('team_away_periods')
        .select(`
          *,
          requester:team_members!team_away_periods_team_member_slack_id_fkey(name,avatar_url),
          backup:team_members!team_away_periods_backup_slack_id_fkey(name,avatar_url)
        `)
        .order('created_at', { ascending: false });
      if (status !== 'all') q = q.eq('status', status);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []).map((r) => {
        const row = r as unknown as AwayPeriod & {
          requester: { name: string | null; avatar_url: string | null } | null;
          backup:    { name: string | null; avatar_url: string | null } | null;
        };
        return {
          ...row,
          requester_name:       row.requester?.name ?? null,
          requester_avatar_url: row.requester?.avatar_url ?? null,
          backup_name:          row.backup?.name ?? null,
          backup_avatar_url:    row.backup?.avatar_url ?? null,
        };
      });
    },
  });
}

/** Admin approve/reject. The status-guard trigger from migration 046
 *  stamps decided_by + decided_at server-side, so we only send the
 *  outcome + the optional note. After the UPDATE we fire two RPCs:
 *  the requester-facing notification (always), and the team-wide
 *  heads-up (only on approve). Both are best-effort — the decision is
 *  the source of truth, notifications are downstream. */
export function useDecideLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      id: string;
      decision: 'approved' | 'rejected';
      note?: string;
    }) => {
      const { data, error } = await supabase
        .from('team_away_periods')
        .update({
          status: args.decision,
          decision_note: args.note?.trim() || null,
        })
        .eq('id', args.id)
        .select()
        .single();
      if (error) throw error;
      const row = data as AwayPeriod;

      // Requester always gets a notification.
      try {
        await supabase.rpc('notify_requester_leave_decided', { p_period_id: row.id });
      } catch { /* swallow */ }

      // Team-wide heads-up only on approve.
      if (args.decision === 'approved') {
        try {
          await supabase.rpc('notify_team_leave_approved', { p_period_id: row.id });
        } catch { /* swallow */ }
      }

      return row;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['all-leave-requests'] });
      qc.invalidateQueries({ queryKey: ['my-away-periods', row.team_member_slack_id] });
      qc.invalidateQueries({ queryKey: ['active-away-periods'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

/** Soft-cancel a request. Used by:
 *    1. Members withdrawing their OWN pending request without
 *       deleting it (keeps the audit trail).
 *    2. Admins cancelling an already-approved period (e.g. the
 *       teammate ended up not taking the leave).
 *  RLS + the status-guard trigger between them allow:
 *    - member: pending → cancelled (only on own row)
 *    - admin:  any → cancelled. */
export function useCancelLeaveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('team_away_periods')
        .update({ status: 'cancelled' as LeaveStatus })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data as AwayPeriod;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['my-away-periods', row.team_member_slack_id] });
      qc.invalidateQueries({ queryKey: ['all-leave-requests'] });
      qc.invalidateQueries({ queryKey: ['active-away-periods'] });
    },
  });
}

/** The set of ticket ids the signed-in user is watching. Lightweight
 *  (just ids) — the dashboard "Watching" tab gets its rows from the
 *  server search, so it only needs membership to keep a row in view
 *  after an optimistic change. Returns a Set for O(1)
 *  `.has()` in the dashboard's per-row group filter. */
export function useMyWatchedTicketIds(slackUserId: string | null | undefined) {
  return useQuery({
    queryKey: ['my-watched-ticket-ids', slackUserId ?? null],
    enabled: !!slackUserId,
    queryFn: async (): Promise<Set<string>> => {
      if (!slackUserId) return new Set();
      const { data, error } = await supabase
        .from('ticket_watchers')
        .select('ticket_id')
        .eq('slack_user_id', slackUserId);
      if (error) throw error;
      return new Set((data ?? []).map((r) => (r as { ticket_id: string }).ticket_id));
    },
    // Watching changes are infrequent; a 60s poll keeps the tab/section
    // fresh without hammering. Toggling watch invalidates this directly.
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
}

/** My Day's ONE source of tickets: the tickets that belong to me (and to the
 *  teammates I'm covering for), my tickets resolved in the last 7 days, and
 *  the top unassigned ones for "Up for grabs".
 *
 *  Why: My Day used to be built from `useTickets` — the newest 500 tickets of
 *  the inbox by creation date — so a ticket you still own but that was created
 *  before the 500th newest (a months-old thread the client keeps replying to)
 *  never showed under My queue / Needs attention / Waiting on client and was
 *  missing from "My open". Everything here is read in full (`pageAll`), so a
 *  ticket is on the page if it is yours, however old.
 *
 *  The key starts with 'tickets' on purpose: optimistic ticket actions patch
 *  every `['tickets', …]` cache and `invalidateTicketLists` refetches it, so
 *  claim / resolve behave exactly as they do for the page. Three plain
 *  queries (not one big `.or()`) so each filter is simple and an error in one
 *  can't be mistaken for another. Archived rows are returned on purpose: the
 *  page filters them itself, as it always did. */
export function useMyDayTickets(args: {
  mySlackId: string | null | undefined;
  /** Me plus the people I cover — see `myDayOwnerIds`. */
  ownerSlackIds: string[];
  inboxIds: string[];
}) {
  const { mySlackId, ownerSlackIds, inboxIds } = args;
  const ownersKey = ownerSlackIds.join(',');
  const inboxKey = inboxIds.join(',');
  return useQuery({
    queryKey: ['tickets', 'my-day', mySlackId ?? null, ownersKey, inboxKey],
    enabled: !!mySlackId && ownerSlackIds.length > 0 && inboxIds.length > 0,
    queryFn: async (): Promise<Ticket[]> => {
      if (!mySlackId) return [];
      const nowMs = Date.now();
      // pageAll: PostgREST cuts one response at 1,000 rows without an error.
      // (`id` as a tie-break keeps the pages from overlapping or skipping.)
      const open = pageAll<Ticket>((from, to) =>
        supabase
          .from('tickets')
          .select('*')
          .in('assignee_slack_id', ownerSlackIds)
          .in('inbox_id', inboxIds)
          .in('status', [...MY_DAY_OPEN_STATUSES])
          // Robot mail waits in the FYI lane, not in anyone's day (migration 080).
          .eq('is_automated_sender', false)
          .order('created_at', { ascending: false })
          .order('id')
          .range(from, to),
      );
      const resolved = pageAll<Ticket>((from, to) =>
        supabase
          .from('tickets')
          .select('*')
          .eq('assignee_slack_id', mySlackId)
          .in('inbox_id', inboxIds)
          .gte('resolved_at', new Date(nowMs - RESOLVED_WINDOW_MS).toISOString())
          .eq('is_automated_sender', false)
          .order('resolved_at', { ascending: false })
          .order('id')
          .range(from, to),
      );
      const grabs = supabase
        .from('tickets')
        .select('*')
        .is('assignee_slack_id', null)
        .in('inbox_id', inboxIds)
        .in('status', [...MY_DAY_ACTIVE_STATUSES])
        .eq('is_automated_sender', false)
        .is('archived_at', null)
        // Still-snoozed tickets are hidden from "Up for grabs".
        .or(`snoozed_until.is.null,snoozed_until.lte.${new Date(nowMs).toISOString()}`)
        .order('priority', { ascending: true })
        .order('created_at', { ascending: true })
        .limit(UP_FOR_GRABS_FETCH);
      const [o, r, g] = await Promise.all([open, resolved, grabs]);
      if (g.error) throw g.error;
      return mergeTicketRows([...o, ...r], (g.data ?? []) as Ticket[]);
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Fetch the FULL Ticket rows for every ticket the signed-in user is
 *  watching — bypasses the 500-row dashboard page so a watched ticket
 *  is never invisible just because it's old or somewhere down the list.
 *
 *  Why this exists separately from `useMyWatchedTicketIds`: the dashboard
 *  + My Day used to intersect the watched-id set against the in-memory
 *  500-row page (the old `useTickets`) to compute the "Watching" list.
 *  That worked while everyone's watched tickets were recent — but
 *  silently broke for any watched ticket that fell off the page
 *  (older than the most recent 500). Worse, the intersect also dropped
 *  archived tickets, so a watched ticket that got auto-archived on
 *  close stopped appearing even when a new client reply revived it.
 *  This hook joins `ticket_watchers` to `tickets` server-side so the
 *  user sees every ticket they explicitly subscribed to, no matter
 *  where it sits in time or archival state.
 *
 *  Returns the raw rows; the caller decides which subset to render
 *  (e.g. My Day filters to non-resolved/closed). 60s poll matches the
 *  ids hook — toggling watch invalidates this directly too. */
export function useMyWatchedTickets(
  slackUserId: string | null | undefined,
  inboxIds?: string[],
) {
  // Structural key — including the inboxIds array keeps the cache split
  // per-inbox so switching between /inbox/support and /inbox/tristan
  // doesn't flash the wrong list.
  const inboxKey = inboxIds && inboxIds.length > 0 ? inboxIds.join(',') : 'all';
  return useQuery({
    queryKey: ['my-watched-tickets', slackUserId ?? null, inboxKey],
    enabled: !!slackUserId,
    queryFn: async (): Promise<Ticket[]> => {
      if (!slackUserId) return [];
      // Two-step query: first pull the watched ticket_ids for this user,
      // then fetch those ticket rows. Could be a single nested-select
      // with a foreign-key join, but the watched-id set is usually small
      // (a few tens at most per user) so two round-trips are cheap and
      // the SQL stays obvious. RLS on both tables guards both reads.
      const idsRes = await supabase
        .from('ticket_watchers')
        .select('ticket_id')
        .eq('slack_user_id', slackUserId);
      if (idsRes.error) throw idsRes.error;
      const ids = (idsRes.data ?? []).map((r) => (r as { ticket_id: string }).ticket_id);
      if (ids.length === 0) return [];
      let q = supabase
        .from('tickets')
        .select('*')
        .in('id', ids);
      // Inbox scope — when the my-day route is rendered inside
      // /inbox/:slug, we narrow watched tickets to that inbox so a
      // watched ticket from support@ doesn't leak into Tristan's
      // Watching section. ticket_watchers itself has no inbox column;
      // the filter lives on the joined tickets row.
      if (inboxIds && inboxIds.length > 0) {
        q = q.in('inbox_id', inboxIds);
      }
      const rowsRes = await q.order('updated_at', { ascending: false });
      if (rowsRes.error) throw rowsRes.error;
      return (rowsRes.data ?? []) as Ticket[];
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
}

// -------------------- Ticket internal notes --------------------
//
// Internal-only commentary visible to all active team members. The DB has
// a trigger on insert that fires add_ticket_notification(...) so assignee
// + watchers get a 'note_added' bell update (excluding the author).

export function useTicketNotes(ticketId: string | undefined) {
  return useQuery({
    queryKey: ['ticket-notes', ticketId],
    enabled: !!ticketId,
    queryFn: async (): Promise<TicketNote[]> => {
      if (!ticketId) return [];
      const { data, error } = await supabase
        .from('ticket_notes')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as TicketNote[];
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
  });
}

/** Shape of an attachment object stored on `ticket_notes.attachments`
 *  (migration 050) — structurally identical to the assist + email
 *  attachment shapes used elsewhere. */
export interface NoteAttachment {
  url: string;
  filename: string;
  contentType: string;
  size: number;
}

export function useAddTicketNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ticketId,
      body,
      authorSlackId,
      attachments,
    }: {
      ticketId: string;
      body: string;
      authorSlackId: string;
      /** Optional. Empty / omitted = text-only note (the common case). */
      attachments?: NoteAttachment[];
    }) => {
      const { data, error } = await supabase
        .from('ticket_notes')
        .insert({
          ticket_id: ticketId,
          author_slack_id: authorSlackId,
          body,
          attachments: (attachments ?? []) as unknown as object,
        })
        .select()
        .single();
      if (error) throw error;
      return data as TicketNote;
    },
    onSuccess: (note) => {
      qc.invalidateQueries({ queryKey: ['ticket-notes', note.ticket_id] });
      // Adding a note triggers a bell notification via DB trigger; refresh.
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
}

export function useDeleteTicketNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ticketId }: { id: string; ticketId: string }) => {
      const { error } = await supabase.from('ticket_notes').delete().eq('id', id);
      if (error) throw error;
      return { id, ticketId };
    },
    onSuccess: ({ ticketId }) => {
      qc.invalidateQueries({ queryKey: ['ticket-notes', ticketId] });
    },
  });
}

// -------------------- Reply templates (saved canned replies) --------------------
//
// Shared team library — any active member can read + manage all rows. Same
// precedent as kb_articles. The composer's slash-menu inserts the body
// after substituting {{first_name}}, {{ticket_subject}}, etc.

export function useReplyTemplates() {
  return useQuery({
    queryKey: ['reply-templates'],
    queryFn: async (): Promise<ReplyTemplate[]> => {
      const { data, error } = await supabase
        .from('reply_templates')
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as ReplyTemplate[];
    },
  });
}

export function useUpsertReplyTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      tpl: Partial<ReplyTemplate> & { name: string; body: string },
    ): Promise<ReplyTemplate> => {
      const { data, error } = await supabase
        .from('reply_templates')
        .upsert(tpl)
        .select()
        .single();
      if (error) throw error;
      return data as ReplyTemplate;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reply-templates'] });
    },
  });
}

export function useDeleteReplyTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('reply_templates').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reply-templates'] });
    },
  });
}

// -------------------- AI quick prompts (per-agent saved instructions) --------------------
//
// One-click chips in the composer's AI bar: a saved instruction ("Ask for a
// screenshot and the login email") the agent can re-use with Draft / Polish.
// Private to the creator (RLS, migration 074). Not a ticket-bearing table, so
// direct writes from the admin UI are allowed (CLAUDE.md section 6).

export type AiQuickPrompt = Tables['ai_quick_prompts']['Row'];

export function useAiQuickPrompts() {
  return useQuery({
    queryKey: ['ai-quick-prompts'],
    queryFn: async (): Promise<AiQuickPrompt[]> => {
      const { data, error } = await supabase
        .from('ai_quick_prompts')
        .select('*')
        .order('label', { ascending: true });
      if (error) throw error;
      return (data ?? []) as AiQuickPrompt[];
    },
    staleTime: 60_000,
  });
}

export function useSaveAiQuickPrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      ownerSlackId: string;
      label: string;
      instruction: string;
    }): Promise<AiQuickPrompt> => {
      const { data, error } = await supabase
        .from('ai_quick_prompts')
        .insert({
          owner_slack_id: input.ownerSlackId,
          label: input.label.trim(),
          instruction: input.instruction.trim(),
        })
        .select()
        .single();
      if (error) {
        // 23505 = unique_violation on (owner, lower(label)).
        if (error.code === '23505') {
          throw new Error('You already have a saved prompt with that name.');
        }
        throw error;
      }
      return data as AiQuickPrompt;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-quick-prompts'] });
    },
  });
}

export function useDeleteAiQuickPrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('ai_quick_prompts').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ai-quick-prompts'] });
    },
  });
}

// Update the signed-in user's own signature_html / signature_text. Scoped
// by email so a malicious caller can't accidentally clobber someone else's
// row even though RLS technically allows it. We resolve the email from the
// active session rather than trusting any client-provided id.
export function useUpdateMySignature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sig: { signature_html: string | null; signature_text: string | null }) => {
      const { data: userResp } = await supabase.auth.getUser();
      const email = userResp.user?.email?.toLowerCase();
      if (!email) throw new Error('Not signed in');
      const { data, error } = await supabase
        .from('team_members')
        .update({
          signature_html: sig.signature_html,
          signature_text: sig.signature_text,
        })
        .eq('email', email)
        .select()
        .single();
      if (error) throw error;
      return data as TeamMember;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-members'] });
      qc.invalidateQueries({ queryKey: ['my-team-member'] });
    },
  });
}

// Resolves the signed-in user's team_members row (by email match). Used by
// the settings page to pre-fill the signature editor and by the composer
// to render a "Signature: <name>" hint.
export function useMyTeamMember() {
  return useQuery({
    queryKey: ['my-team-member'],
    queryFn: async (): Promise<TeamMember | null> => {
      const { data: userResp } = await supabase.auth.getUser();
      const email = userResp.user?.email?.toLowerCase();
      if (!email) return null;
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('email', email)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as TeamMember | null;
    },
  });
}

export function useUpsertTeamMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: Partial<TeamMember> & { slack_user_id: string; email: string; name: string },
    ): Promise<{ member: TeamMember; created: boolean }> => {
      // Check whether the row already exists so we can return `created:true`
      // on the first insert and the caller can decide whether to fire an
      // invitation email. Doing this in two queries instead of relying on
      // PostgREST's onConflict shape because we need the boolean.
      const { data: existing } = await supabase
        .from('team_members')
        .select('slack_user_id')
        .eq('slack_user_id', input.slack_user_id)
        .maybeSingle();
      const created = !existing;

      const { data, error } = await supabase
        .from('team_members')
        .upsert(input, { onConflict: 'slack_user_id' })
        .select()
        .single();
      if (error) throw error;
      return { member: data as TeamMember, created };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-members'] });
    },
  });
}

/** Send (or resend) the invitation email for a team_member. Calls the
 *  `send-team-invite` Edge Function which validates the caller is an
 *  active admin, sends a templated email via Gmail OAuth, and stamps
 *  team_members.invited_at = now() so the UI can show "Just invited".
 *
 *  Used by:
 *    1. The Team page's "Resend invite" button on pending rows.
 *    2. Automatically fired by the Team page after a successful insert
 *       (see `useUpsertTeamMember` callers — `created === true`). */
export function useResendTeamInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slack_user_id: string): Promise<{ ok: boolean; message_id?: string }> => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Not signed in');
      const r = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL.replace(/\/$/, '')}/functions/v1/send-team-invite`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ slack_user_id }),
        },
      );
      const j = await r.json();
      if (!r.ok || !j.ok) {
        throw new Error(j.error ?? `send-team-invite ${r.status}`);
      }
      return j;
    },
    onSuccess: () => {
      // Bump cached team_members so invited_at refreshes in the UI.
      qc.invalidateQueries({ queryKey: ['team-members'] });
    },
  });
}

// ---------------------------------------------------------------------------
// system_settings — key/value config the team can edit (migration 030).
// Workflow 05 reads `auto_followup_nudge_days` and `auto_followup_close_days`
// at the start of every run.
// ---------------------------------------------------------------------------

export interface AutoFollowupSettings {
  nudge_days: number;
  close_days: number;
}

const AUTO_FOLLOWUP_DEFAULTS: AutoFollowupSettings = {
  nudge_days: 4,
  close_days: 10,
};

/** Load the two auto-follow-up day counts as a single object. Defaults match
 *  workflow 05's hardcoded fallback so first-load before the migration is
 *  applied still renders sensibly. */
export function useAutoFollowupSettings() {
  return useQuery({
    queryKey: ['system-settings', 'auto-followup'],
    queryFn: async (): Promise<AutoFollowupSettings> => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('key,value')
        .in('key', ['auto_followup_nudge_days', 'auto_followup_close_days']);
      if (error) throw error;
      const out = { ...AUTO_FOLLOWUP_DEFAULTS };
      for (const row of (data ?? []) as { key: string; value: unknown }[]) {
        const n = typeof row.value === 'number' ? row.value : Number(row.value);
        if (Number.isFinite(n)) {
          if (row.key === 'auto_followup_nudge_days') out.nudge_days = n;
          if (row.key === 'auto_followup_close_days') out.close_days = n;
        }
      }
      return out;
    },
  });
}

// ---------------------------------------------------------------------------
// Per-user email-notification preferences (migration 031). Default values
// here MUST match the SQL DEFAULT in team_members.email_notification_prefs
// so the UI doesn't show a different state than what the RPC actually uses.
// ---------------------------------------------------------------------------

export type NotificationPrefKey = NotificationType;

export interface EmailNotificationPrefs {
  client_replied: boolean;
  sla_breach: boolean;
  follow_up_sent: boolean;
  /** Your own "remind me" came due. Migration 082. */
  follow_up_reminder: boolean;
  reassigned_to_you: boolean;
  /** An automation rule assigned a new ticket to you at intake. Migration 071. */
  new_ticket_assigned: boolean;
  ticket_escalated: boolean;
  ticket_resolved: boolean;
  note_added: boolean;
  note_mention: boolean;
  /** Daily 08:00 Sydney digest (Mon–Fri). Migration 034. */
  morning_digest: boolean;
  /** Someone added you as a watcher on a ticket. Migration 070. */
  added_as_watcher: boolean;
}

// Everything is ON by default (migration 071 set the same defaults on the
// column and backfilled every member). Members opt OUT per type in Settings.
export const EMAIL_NOTIFICATION_DEFAULTS: EmailNotificationPrefs = {
  client_replied: true,
  sla_breach: true,
  reassigned_to_you: true,
  new_ticket_assigned: true,
  ticket_escalated: true,
  note_mention: true,
  ticket_resolved: true,
  follow_up_sent: true,
  follow_up_reminder: true,
  note_added: true,
  morning_digest: true,
  added_as_watcher: true,
};

/** Resolve the signed-in user's email-notification prefs as a fully
 *  populated object (defaults filled in for any missing keys). */
export function useMyEmailNotificationPrefs() {
  return useQuery({
    queryKey: ['my-email-notification-prefs'],
    queryFn: async (): Promise<EmailNotificationPrefs> => {
      const { data: userResp } = await supabase.auth.getUser();
      const email = userResp.user?.email?.toLowerCase();
      if (!email) return EMAIL_NOTIFICATION_DEFAULTS;
      const { data, error } = await supabase
        .from('team_members')
        .select('email_notification_prefs')
        .eq('email', email)
        .maybeSingle();
      if (error) throw error;
      const stored = (data?.email_notification_prefs ?? {}) as Partial<EmailNotificationPrefs>;
      // Merge over defaults so newly added types automatically light up
      // the user's preference toggles without requiring a DB migration.
      return { ...EMAIL_NOTIFICATION_DEFAULTS, ...stored };
    },
  });
}

export function useUpdateMyEmailNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (next: EmailNotificationPrefs) => {
      const { data: userResp } = await supabase.auth.getUser();
      const email = userResp.user?.email?.toLowerCase();
      if (!email) throw new Error('Not signed in');
      const { error } = await supabase
        .from('team_members')
        .update({ email_notification_prefs: next })
        .eq('email', email);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-email-notification-prefs'] });
      qc.invalidateQueries({ queryKey: ['my-team-member'] });
    },
  });
}

/** Save both day counts. Validates ordering client-side; the workflow has
 *  its own guard but we want a fast UI-side error. */
export function useUpdateAutoFollowupSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (next: AutoFollowupSettings) => {
      if (!Number.isInteger(next.nudge_days) || next.nudge_days < 1) {
        throw new Error('Nudge days must be a whole number ≥ 1.');
      }
      if (!Number.isInteger(next.close_days) || next.close_days <= next.nudge_days) {
        throw new Error('Close days must be a whole number greater than nudge days.');
      }
      if (next.close_days > 60) {
        throw new Error('Close days capped at 60 (longer windows almost always mean the ticket is dead).');
      }
      // Two upserts in parallel. We keep them as separate rows (rather than
      // packing into one jsonb object) so future settings can join the
      // table without schema changes — see migration 030 comment.
      const rows = [
        { key: 'auto_followup_nudge_days', value: next.nudge_days },
        { key: 'auto_followup_close_days', value: next.close_days },
      ];
      const { error } = await supabase
        .from('system_settings')
        .upsert(rows, { onConflict: 'key' });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['system-settings', 'auto-followup'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Team groups (migration 032). Just metadata for now — we'll layer ticket
// routing / group SLAs on top in later passes.
// ---------------------------------------------------------------------------

export interface TeamGroup {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TeamGroupWithCount extends TeamGroup {
  member_count: number;
}

export interface TeamGroupMember {
  group_id: string;
  slack_user_id: string;
  added_at: string;
}

/** Slugify on the client so the user can type a name and we round-trip a
 *  stable URL slug. Mirrors postgres' typical slug pattern (kebab, ASCII). */
export function slugifyGroupName(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

export function useTeamGroups() {
  return useQuery({
    queryKey: ['team-groups'],
    queryFn: async (): Promise<TeamGroupWithCount[]> => {
      const { data, error } = await supabase
        .from('team_groups')
        .select('*, team_group_members(count)')
        .eq('active', true)
        .order('name');
      if (error) throw error;
      // PostgREST nests aggregates as an array of one object — flatten it.
      return ((data ?? []) as Array<TeamGroup & { team_group_members: { count: number }[] }>).map(
        (g) => ({
          ...g,
          member_count: g.team_group_members?.[0]?.count ?? 0,
        }),
      );
    },
  });
}

/** Pull the IDs of KB articles the triage step suggested for a given
 *  ticket. Workflow 01 stores these in `ticket_events.payload.suggested_kb_article_ids`
 *  on the row with event_type='triaged'. Returns [] if no triage event
 *  exists yet (cold lookup) or the array was empty. */
export function useTicketKbSuggestionIds(ticketId: string | undefined) {
  return useQuery({
    queryKey: ['ticket-kb-suggestions', ticketId],
    enabled: !!ticketId,
    queryFn: async (): Promise<string[]> => {
      if (!ticketId) return [];
      const { data, error } = await supabase
        .from('ticket_events')
        .select('payload')
        .eq('ticket_id', ticketId)
        .eq('event_type', 'triaged')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      const ids = (data?.payload as { suggested_kb_article_ids?: unknown } | null)
        ?.suggested_kb_article_ids;
      if (!Array.isArray(ids)) return [];
      return ids.filter((id): id is string => typeof id === 'string');
    },
  });
}

/** All group→member rows in one shot, keyed by group_id. Used by the
 *  reports filter so picking one or more groups can resolve to the
 *  combined member set without N round-trips. */
export function useAllTeamGroupMemberships() {
  return useQuery({
    queryKey: ['team-group-memberships-all'],
    queryFn: async (): Promise<Map<string, string[]>> => {
      const { data, error } = await supabase
        .from('team_group_members')
        .select('group_id, slack_user_id');
      if (error) throw error;
      const out = new Map<string, string[]>();
      for (const row of (data ?? []) as Array<{ group_id: string; slack_user_id: string }>) {
        const arr = out.get(row.group_id) ?? [];
        arr.push(row.slack_user_id);
        out.set(row.group_id, arr);
      }
      return out;
    },
  });
}

export function useTeamGroupMembers(groupId: string | null) {
  return useQuery({
    queryKey: ['team-group-members', groupId],
    enabled: !!groupId,
    queryFn: async (): Promise<TeamMember[]> => {
      if (!groupId) return [];
      const { data, error } = await supabase
        .from('team_group_members')
        .select('slack_user_id, added_at, team_members!inner(*)')
        .eq('group_id', groupId)
        .order('added_at', { ascending: true });
      if (error) throw error;
      // PostgREST returns the joined row as a `team_members` array (one
      // entry, since the FK is one-to-one). Defensively pull either the
      // first array element or the object itself depending on how the
      // generated types resolved this.
      const rows = (data ?? []) as unknown as Array<{ team_members: TeamMember | TeamMember[] }>;
      return rows
        .map((r) => (Array.isArray(r.team_members) ? r.team_members[0] : r.team_members))
        .filter((tm): tm is TeamMember => !!tm);
    },
  });
}

export function useCreateTeamGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; description?: string | null }) => {
      const name = input.name.trim();
      if (!name) throw new Error('Name is required');
      const slug = slugifyGroupName(name);
      if (!slug) throw new Error('Name must contain at least one alphanumeric character.');
      const { data, error } = await supabase
        .from('team_groups')
        .insert({ name, slug, description: input.description?.trim() || null })
        .select()
        .single();
      if (error) throw error;
      return data as TeamGroup;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-groups'] });
    },
  });
}

export function useUpdateTeamGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      name?: string;
      description?: string | null;
    }) => {
      const patch: Partial<TeamGroup> = {};
      if (input.name !== undefined) {
        const name = input.name.trim();
        if (!name) throw new Error('Name is required');
        patch.name = name;
        patch.slug = slugifyGroupName(name);
      }
      if (input.description !== undefined) {
        patch.description = input.description?.trim() || null;
      }
      const { error } = await supabase
        .from('team_groups')
        .update(patch)
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-groups'] });
    },
  });
}

/** Soft-delete: set active=false. We keep the row (and the FK from
 *  team_group_members) so historical references remain readable. The list
 *  query already filters on active. */
export function useDeleteTeamGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('team_groups')
        .update({ active: false })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-groups'] });
    },
  });
}

export function useAddTeamGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { group_id: string; slack_user_id: string }) => {
      const { error } = await supabase
        .from('team_group_members')
        .insert(input);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['team-groups'] });
      qc.invalidateQueries({ queryKey: ['team-group-members', vars.group_id] });
    },
  });
}

export function useRemoveTeamGroupMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { group_id: string; slack_user_id: string }) => {
      const { error } = await supabase
        .from('team_group_members')
        .delete()
        .eq('group_id', input.group_id)
        .eq('slack_user_id', input.slack_user_id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['team-groups'] });
      qc.invalidateQueries({ queryKey: ['team-group-members', vars.group_id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Saved dashboard views (migration 033). Per-user array of {id, name, search}
// stored as jsonb on team_members. The dashboard route reads its filter
// state from the URL searchParams, so a "view" is just the URL query
// string we replay via navigate().
// ---------------------------------------------------------------------------

export interface SavedDashboardView {
  id: string;
  name: string;
  /** URL query string (without leading ?). Restored via navigate('/dashboard?' + search). */
  search: string;
}

export function useMyDashboardViews() {
  return useQuery({
    queryKey: ['my-dashboard-views'],
    queryFn: async (): Promise<SavedDashboardView[]> => {
      const { data: userResp } = await supabase.auth.getUser();
      const email = userResp.user?.email?.toLowerCase();
      if (!email) return [];
      const { data, error } = await supabase
        .from('team_members')
        .select('saved_dashboard_views')
        .eq('email', email)
        .maybeSingle();
      if (error) throw error;
      const raw = (data?.saved_dashboard_views as unknown) ?? [];
      if (!Array.isArray(raw)) return [];
      // Defensive shape check — bad data in jsonb shouldn't crash the UI.
      return raw.filter(
        (v): v is SavedDashboardView =>
          v != null &&
          typeof v === 'object' &&
          typeof (v as SavedDashboardView).id === 'string' &&
          typeof (v as SavedDashboardView).name === 'string' &&
          typeof (v as SavedDashboardView).search === 'string',
      );
    },
  });
}

export function useSaveDashboardView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; search: string }) => {
      const { data: userResp } = await supabase.auth.getUser();
      const email = userResp.user?.email?.toLowerCase();
      if (!email) throw new Error('Not signed in');
      const name = input.name.trim();
      if (!name) throw new Error('View name is required');

      // Read-modify-write the jsonb. Concurrency is fine at this scale —
      // saved views aren't an inter-user-shared resource.
      const { data: row, error: readErr } = await supabase
        .from('team_members')
        .select('saved_dashboard_views')
        .eq('email', email)
        .maybeSingle();
      if (readErr) throw readErr;
      const existing = Array.isArray(row?.saved_dashboard_views)
        ? (row?.saved_dashboard_views as SavedDashboardView[])
        : [];
      const newView: SavedDashboardView = {
        id: crypto.randomUUID(),
        name,
        search: input.search,
      };
      // If a view with the same name already exists, replace it (so users
      // can "update" by re-saving with the same name).
      const filtered = existing.filter(
        (v) => v.name.trim().toLowerCase() !== name.toLowerCase(),
      );
      const next = [...filtered, newView].slice(-20); // cap at 20 to keep jsonb small
      const { error: writeErr } = await supabase
        .from('team_members')
        .update({ saved_dashboard_views: next })
        .eq('email', email);
      if (writeErr) throw writeErr;
      return newView;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-dashboard-views'] });
    },
  });
}

export function useDeleteDashboardView() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data: userResp } = await supabase.auth.getUser();
      const email = userResp.user?.email?.toLowerCase();
      if (!email) throw new Error('Not signed in');
      const { data: row, error: readErr } = await supabase
        .from('team_members')
        .select('saved_dashboard_views')
        .eq('email', email)
        .maybeSingle();
      if (readErr) throw readErr;
      const existing = Array.isArray(row?.saved_dashboard_views)
        ? (row?.saved_dashboard_views as SavedDashboardView[])
        : [];
      const next = existing.filter((v) => v.id !== id);
      const { error: writeErr } = await supabase
        .from('team_members')
        .update({ saved_dashboard_views: next })
        .eq('email', email);
      if (writeErr) throw writeErr;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-dashboard-views'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Reply tone check — Edge Function `check-reply-tone`. Called debounced
// from the composer; never blocks Send. Cached per body string so editing
// + reverting doesn't re-hit the API. (See the function's comment for the
// auth model and Anthropic prompt.)
// ---------------------------------------------------------------------------

export interface ToneCheckResult {
  ok: boolean;
  tone: 'good' | 'concerning';
  concerns: { severity: 'low' | 'medium' | 'high'; message: string }[];
}

/** Manual tone check. Caller debounces. Disabled when body is short. */
export function useReplyToneCheck(body: string, enabled = true) {
  const trimmed = body.trim();
  return useQuery({
    queryKey: ['reply-tone-check', trimmed],
    enabled: enabled && trimmed.length >= 30,
    // 5-minute stale time — same body shouldn't re-hit Anthropic on idle
    // re-renders. Composer re-edits invalidate by changing the key.
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ToneCheckResult> => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Not signed in');
      const r = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL.replace(/\/$/, '')}/functions/v1/check-reply-tone`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ body: trimmed }),
        },
      );
      if (!r.ok) throw new Error(`tone_check_${r.status}`);
      const j = (await r.json()) as ToneCheckResult;
      return j;
    },
  });
}

// ---------------------------------------------------------------------------
// Thread summary — Edge Function `summarize-thread`. Lazy-loaded by the
// "Summary" tab on the ticket detail page. Caching strategy: the query
// key includes the latest message id, so when a new reply arrives the
// cached summary auto-invalidates. Within a single message-set, repeat
// opens of the tab are free (5 minute stale window).
// ---------------------------------------------------------------------------

export interface ThreadSummary {
  tldr: string;
  client_intent: string;
  what_we_tried: string;
  open_questions: string;
  next_step: string;
}

export function useThreadSummary(
  ticketId: string | undefined,
  latestMessageId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['thread-summary', ticketId, latestMessageId],
    enabled: enabled && !!ticketId && !!latestMessageId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ThreadSummary> => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Not signed in');
      const r = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL.replace(/\/$/, '')}/functions/v1/summarize-thread`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ ticket_id: ticketId }),
        },
      );
      const j = (await r.json()) as { ok: boolean; summary?: ThreadSummary; error?: string };
      if (!r.ok || !j.ok || !j.summary) {
        throw new Error(j.error ?? `summarize-thread ${r.status}`);
      }
      return j.summary;
    },
  });
}

// ---------------------------------------------------------------------------
// Per-attachment retry — Edge Function `retry-attachment`. Used by the
// FailedAttachmentCluster's Retry button to recover a single null-url
// attachment without the user having to open a terminal.
// ---------------------------------------------------------------------------

export interface RetryAttachmentInput {
  message_id: string;
  attachment_index: number;
}
export interface RetryAttachmentResult {
  ok: boolean;
  url?: string;
  filename?: string;
  size_bytes?: number;
}

export function useRetryAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RetryAttachmentInput): Promise<RetryAttachmentResult> => {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error('Not signed in');
      const r = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL.replace(/\/$/, '')}/functions/v1/retry-attachment`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(input),
        },
      );
      const j = (await r.json()) as { ok: boolean; url?: string; filename?: string; size_bytes?: number; error?: string };
      if (!r.ok || !j.ok) {
        throw new Error(j.error ?? `retry-attachment ${r.status}`);
      }
      return j as RetryAttachmentResult;
    },
    onSuccess: (_, vars) => {
      // Invalidate the ticket-messages cache so the row re-fetches with
      // the new URL. The caller can also do an optimistic update if it
      // wants to skip the round-trip.
      qc.invalidateQueries({ queryKey: ['ticket-messages'] });
      void vars;
    },
  });
}

// ---------------------------------------------------------------------------
// Company branding (migration 044). Singleton row at id=1. Logo file lives
// in the public `branding` bucket; we resolve the public URL on the fly so
// any cache-busting (different filename per upload) propagates immediately.
//
// Read: any active team member. Write: admins only — RLS enforces it. The
// UI also disables the upload control for non-admins as a courtesy.
// ---------------------------------------------------------------------------

/** Loaded branding plus a resolved `logo_url` (or null if no logo set).
 *  The hook returns `null` for `logo_url` rather than the raw path so
 *  callers can render a fallback wordmark without re-deriving the URL. */
export interface CompanyBranding {
  logo_path: string | null;
  logo_mime: string | null;
  logo_url: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

export function useCompanyBranding() {
  return useQuery({
    queryKey: ['company-branding'],
    // Long-lived: branding rarely changes. The upload mutation invalidates
    // this key, so we don't need to chase it.
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CompanyBranding> => {
      const { data, error } = await supabase
        .from('company_settings')
        .select('logo_path,logo_mime,updated_at,updated_by')
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      const path = data?.logo_path ?? null;
      const url = path
        ? supabase.storage.from('branding').getPublicUrl(path).data.publicUrl
        : null;
      return {
        logo_path: path,
        logo_mime: data?.logo_mime ?? null,
        logo_url: url,
        updated_at: data?.updated_at ?? null,
        updated_by: data?.updated_by ?? null,
      };
    },
  });
}

/** Upload a new logo. Steps:
 *    1. Validate type + size (cheap client guard; RLS + bucket settings are
 *       the real lock).
 *    2. PUT to `branding/logo-<ts>.<ext>` so each upload gets a unique path
 *       — defeats CDN caches without query-string tricks.
 *    3. UPDATE company_settings (singleton id=1).
 *    4. Best-effort delete of the previous file so the bucket doesn't
 *       accumulate orphans. Failures here are non-fatal; the new logo is
 *       already live.
 */
export function useUploadCompanyLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<CompanyBranding> => {
      const MAX_BYTES = 2 * 1024 * 1024; // 2 MB
      const ALLOWED = new Set(['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp']);
      if (!ALLOWED.has(file.type)) {
        throw new Error(`Unsupported file type ${file.type || '(unknown)'}. Use PNG, JPG, SVG, or WebP.`);
      }
      if (file.size > MAX_BYTES) {
        throw new Error(`Logo is ${(file.size / 1024 / 1024).toFixed(2)} MB — max 2 MB.`);
      }

      // 1. Resolve current path so we can clean it up after the swap.
      const { data: cur } = await supabase
        .from('company_settings')
        .select('logo_path')
        .eq('id', 1)
        .maybeSingle();
      const previousPath = cur?.logo_path ?? null;

      // 2. Upload. Filename = logo-<unix-ms>.<ext>. ext mapping covers the
      //    four allowed mime types; falls back to `bin` for safety (won't
      //    happen due to the guard above).
      const extByMime: Record<string, string> = {
        'image/png': 'png',
        'image/jpeg': 'jpg',
        'image/svg+xml': 'svg',
        'image/webp': 'webp',
      };
      const ext = extByMime[file.type] ?? 'bin';
      const path = `logo-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('branding')
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw upErr;

      // 3. Resolve the public URL and the signed-in email (for audit).
      const publicUrl = supabase.storage.from('branding').getPublicUrl(path).data.publicUrl;
      const { data: userResp } = await supabase.auth.getUser();
      const email = userResp.user?.email?.toLowerCase() ?? null;

      const { error: updErr } = await supabase
        .from('company_settings')
        .update({ logo_path: path, logo_mime: file.type, updated_by: email })
        .eq('id', 1);
      if (updErr) {
        // Best-effort rollback of the just-uploaded file so we don't leave
        // an orphan when the row update fails (RLS, etc).
        await supabase.storage.from('branding').remove([path]).catch(() => {});
        throw updErr;
      }

      // 4. Best-effort cleanup of the previous logo. Don't await the
      //    failure path — we want the mutation to resolve successfully
      //    even if the old file is already gone or RLS quirks block the
      //    delete.
      if (previousPath && previousPath !== path) {
        void supabase.storage.from('branding').remove([previousPath]).catch(() => {});
      }

      return {
        logo_path: path,
        logo_mime: file.type,
        logo_url: publicUrl,
        updated_at: new Date().toISOString(),
        updated_by: email,
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['company-branding'] });
    },
  });
}

/** Clear the logo (revert to text wordmark). Removes the storage file +
 *  nulls the row columns. Same gating — admins only. */
export function useClearCompanyLogo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const { data: cur } = await supabase
        .from('company_settings')
        .select('logo_path')
        .eq('id', 1)
        .maybeSingle();
      const previousPath = cur?.logo_path ?? null;

      const { data: userResp } = await supabase.auth.getUser();
      const email = userResp.user?.email?.toLowerCase() ?? null;

      const { error } = await supabase
        .from('company_settings')
        .update({ logo_path: null, logo_mime: null, updated_by: email })
        .eq('id', 1);
      if (error) throw error;

      if (previousPath) {
        void supabase.storage.from('branding').remove([previousPath]).catch(() => {});
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['company-branding'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Customer Assist live sessions (migration 047). One row per session,
// keyed on a 256-bit token. The agent SPA reads the table via the
// authenticated client (RLS gate: `is_active_team_member()`). The client
// /assist/<token> page uses a SEPARATE Supabase client with the
// `x-assist-token` header so the anon SELECT policy lets it through —
// that wiring lives in the assist route, not here.
// ---------------------------------------------------------------------------

export type AssistSessionStatus = 'pending' | 'active' | 'ended' | 'expired';

export type AssistSession = Omit<Tables['customer_assist_sessions']['Row'], 'status'> & {
  status: AssistSessionStatus;
};

/** Resolve any currently-live session for a ticket. Returns null if none
 *  — the agent UI uses this to choose between "Request live assist"
 *  (start a new one) and "Open existing session" (reuse the one already
 *  in flight). Polls every 60s so the agent sees expiry happen without
 *  reloading. */
export function useActiveAssistSession(ticketId: string | null | undefined) {
  return useQuery({
    queryKey: ['active-assist-session', ticketId ?? null],
    enabled: !!ticketId,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<AssistSession | null> => {
      if (!ticketId) return null;
      const { data, error } = await supabase
        .from('customer_assist_sessions')
        .select('*')
        .eq('ticket_id', ticketId)
        .in('status', ['pending', 'active'])
        // The partial unique index guarantees at most one row here, but
        // we order + limit defensively in case of a race during status
        // transitions.
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as AssistSession | null;
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Multi-inbox hooks (Phase A — migration 060)
// ─────────────────────────────────────────────────────────────────────────────
// gmail_inboxes — one row per Gmail account ingested.
// team_inbox_access — junction granting (member × inbox) with a role per row.
//
// Reads pass through RLS (your own rows, or everyone's with team.view).
// Grants are written through the set_member_access / invite_member RPCs
// (see useSetMemberAccess below), which check the guard rules in one
// transaction and write the audit log. The older direct upsert/delete
// hooks were removed in migration 078's rollout: a grant without a role
// is exactly what the role model replaced.

export type GmailInbox = Tables['gmail_inboxes']['Row'];
export type GmailInboxEdit = Omit<
  Tables['gmail_inboxes']['Insert'],
  'id' | 'created_at'
>;
export type TeamInboxAccessRow = Tables['team_inbox_access']['Row'];

/** Every gmail_inbox the signed-in user can see (admin → all rows). Ordered
 *  primary-first then active-first then alphabetical so the /inboxes
 *  page reads sensibly. Stale time short — admin actions on the list
 *  should feel immediate. */
export function useGmailInboxes() {
  return useQuery({
    queryKey: ['gmail-inboxes'],
    staleTime: 10_000,
    queryFn: async (): Promise<GmailInbox[]> => {
      const { data, error } = await supabase
        .from('gmail_inboxes')
        .select('*')
        .order('is_primary', { ascending: false })
        .order('active', { ascending: false })
        .order('display_name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as GmailInbox[];
    },
  });
}

/** Per-inbox count of NEW (status='new', non-archived, not currently snoozed)
 *  tickets — drives the unread-style badges on the inbox switcher. RLS scopes
 *  the underlying query to inboxes the caller can access, so the returned map
 *  only ever contains accessible inboxes (revoking access drops an inbox from
 *  the map on the next refetch, same as everywhere else). Returns
 *  Map<inbox_id, count>. Auto-refreshes every 60s so the badge stays live. */
export function useInboxNewCounts() {
  return useQuery({
    queryKey: ['inbox-new-counts'],
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<Map<string, number>> => {
      const nowIso = new Date().toISOString();
      // One exact COUNT per inbox. This used to select every "new" row and count
      // them here, which PostgREST silently cut at 1,000 — so with more than a
      // thousand new tickets the badges under-counted.
      const inboxes = await supabase.from('gmail_inboxes').select('id');
      if (inboxes.error) throw inboxes.error;
      const ids = ((inboxes.data ?? []) as Array<{ id: string }>).map((r) => r.id);
      const counted = await Promise.all(
        ids.map(async (id) => {
          const { count, error } = await supabase
            .from('tickets')
            .select('id', { count: 'exact', head: true })
            .eq('inbox_id', id)
            .eq('status', 'new')
            // Robot mail is in the FYI lane, so it doesn't count as "new" work.
            .eq('is_automated_sender', false)
            .is('archived_at', null)
            // Exclude tickets still snoozed into the future — they're hidden from
            // the New view, so they shouldn't inflate the badge.
            .or(`snoozed_until.is.null,snoozed_until.lte.${nowIso}`);
          if (error) throw error;
          return [id, count ?? 0] as const;
        }),
      );
      const m = new Map<string, number>();
      for (const [id, n] of counted) if (n > 0) m.set(id, n);
      return m;
    },
  });
}

/** Status of an inbox's OAuth refresh token. Returns
 *  { has_token, updated_at, last_synced_to_n8n_at } — never the token
 *  value. Backed by the admin-only inbox_credential_status RPC
 *  (migration 061/062). */
export interface InboxCredentialStatus {
  has_token: boolean;
  updated_at: string | null;
  last_synced_to_n8n_at: string | null;
}

export function useInboxCredentialStatus(inboxId: string | null | undefined) {
  return useQuery({
    queryKey: ['inbox-credential-status', inboxId ?? null],
    enabled: !!inboxId,
    queryFn: async (): Promise<InboxCredentialStatus> => {
      if (!inboxId) return { has_token: false, updated_at: null, last_synced_to_n8n_at: null };
      const { data, error } = await supabase.rpc('inbox_credential_status', { p_inbox_id: inboxId });
      if (error) throw error;
      return (data ?? { has_token: false, updated_at: null, last_synced_to_n8n_at: null }) as InboxCredentialStatus;
    },
  });
}

/** One-click "wire this inbox up in n8n" — calls the sync-inbox-to-n8n
 *  Edge Function, which provisions the n8n credential + adds the Gmail
 *  Trigger + Tag nodes to workflow 01 + activates. After a successful
 *  call, the inbox's credential status flips to "Ready" because the
 *  Edge Function stamps `last_synced_to_n8n_at`. */
export function useSyncInboxToN8n() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (inbox_id: string) => {
      const { postSyncInboxToN8n } = await import('@/lib/api');
      return postSyncInboxToN8n(inbox_id);
    },
    onSuccess: (_data, inbox_id) => {
      qc.invalidateQueries({ queryKey: ['inbox-credential-status', inbox_id] });
      qc.invalidateQueries({ queryKey: ['gmail-inboxes'] });
    },
  });
}

/** Store / rotate the OAuth refresh token for an inbox. The token is
 *  encrypted into Supabase Vault via the admin-only
 *  set_inbox_refresh_token RPC and the SPA never gets it back; the
 *  status query above shows only whether one exists. */
export function useSetInboxRefreshToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { inbox_id: string; token: string }) => {
      const { error } = await supabase.rpc('set_inbox_refresh_token', {
        p_inbox_id: args.inbox_id,
        p_token: args.token,
      });
      if (error) throw error;
    },
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: ['inbox-credential-status', args.inbox_id] });
    },
  });
}

/** Create-or-update a gmail_inbox row. Insert when id is omitted, update
 *  by id otherwise. is_primary is intentionally NOT settable here — the
 *  primary inbox is locked at the schema level (partial unique index +
 *  seeded once in migration 060). Re-pointing primary is a deliberate
 *  one-shot operation; if you ever need it, write a follow-up migration. */
export function useUpsertGmailInbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      args: GmailInboxEdit & { id?: string },
    ): Promise<GmailInbox> => {
      const { id, ...patch } = args;
      // Strip is_primary so callers can't flip the flag from the SPA.
      delete (patch as Record<string, unknown>).is_primary;
      if (id) {
        const { data, error } = await supabase
          .from('gmail_inboxes')
          .update(patch)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as GmailInbox;
      }
      const { data, error } = await supabase
        .from('gmail_inboxes')
        .insert(patch)
        .select('*')
        .single();
      if (error) throw error;
      return data as GmailInbox;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['gmail-inboxes'] });
    },
  });
}

// ─── Automation rules (admin-only; RLS-enforced) ─────────────────────────────
// CRUD for automation_rules + read of automation_rule_runs (audit) + the
// global kill switch on company_settings.automations_enabled. conditions /
// actions are jsonb in the DB; we surface them as the strongly-typed engine
// shapes so the editor + dry-run consume one type.

type AutomationRuleRow = Tables['automation_rules']['Row'];
export type AutomationRuleRun = Tables['automation_rule_runs']['Row'];

export type AutomationRule = Omit<AutomationRuleRow, 'conditions' | 'actions' | 'match_type'> & {
  match_type: RuleMatchType;
  conditions: RuleCondition[];
  actions: RuleAction[];
};

/** future = act on new tickets; existing = one-off apply to current matches;
 *  both = new + existing. 'existing'/'both' enable the bulk-apply action. */
export type AutomationScope = 'future' | 'existing' | 'both';

export interface AutomationRuleInput {
  id?: string;
  name: string;
  description?: string | null;
  enabled?: boolean;
  scope?: AutomationScope;
  match_type: RuleMatchType;
  conditions: RuleCondition[];
  actions: RuleAction[];
  inbox_id?: string | null;
  priority?: number;
  /** Set on create only (FK to team_members.slack_user_id). */
  created_by?: string | null;
}

function castRule(row: AutomationRuleRow): AutomationRule {
  return {
    ...row,
    match_type: (row.match_type === 'any' ? 'any' : 'all') as RuleMatchType,
    conditions: (row.conditions as unknown as RuleCondition[]) ?? [],
    actions: (row.actions as unknown as RuleAction[]) ?? [],
  };
}

export function useAutomationRules() {
  return useQuery({
    queryKey: ['automation-rules'],
    staleTime: 10_000,
    queryFn: async (): Promise<AutomationRule[]> => {
      const { data, error } = await supabase
        .from('automation_rules')
        .select('*')
        .order('priority', { ascending: true })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []).map((r) => castRule(r as AutomationRuleRow));
    },
  });
}

export function useUpsertAutomationRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AutomationRuleInput): Promise<AutomationRule> => {
      const { id, created_by, ...rest } = input;
      // jsonb columns accept the arrays directly via supabase-js serialisation.
      const patch: Record<string, unknown> = {
        name: rest.name,
        description: rest.description ?? null,
        match_type: rest.match_type,
        conditions: rest.conditions,
        actions: rest.actions,
        inbox_id: rest.inbox_id ?? null,
        priority: rest.priority ?? 100,
        ...(rest.scope ? { scope: rest.scope } : {}),
        // enabled is only set explicitly; on create it defaults to false (DB).
        ...(rest.enabled !== undefined ? { enabled: rest.enabled } : {}),
      };
      if (id) {
        patch.updated_at = new Date().toISOString();
        const { data, error } = await supabase
          .from('automation_rules')
          .update(patch)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return castRule(data as AutomationRuleRow);
      }
      if (created_by) patch.created_by = created_by;
      const { data, error } = await supabase
        .from('automation_rules')
        .insert(patch)
        .select('*')
        .single();
      if (error) throw error;
      return castRule(data as AutomationRuleRow);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automation-rules'] });
    },
  });
}

export function useToggleAutomationRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, enabled }: { id: string; enabled: boolean }): Promise<void> => {
      const { error } = await supabase
        .from('automation_rules')
        .update({ enabled, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automation-rules'] });
    },
  });
}

export function useDeleteAutomationRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      const { error } = await supabase.from('automation_rules').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automation-rules'] });
    },
  });
}

/** A run row enriched with the ticket's current headline fields (embedded via
 *  the ticket_id FK; RLS-scoped, so a ticket outside the admin's inboxes
 *  surfaces as null). */
export type AutomationRuleRunWithTicket = AutomationRuleRun & {
  ticket: {
    id: string;
    subject: string | null;
    sender_email: string | null;
    status: string | null;
    priority: string | null;
    assignee_slack_id: string | null;
  } | null;
};

/** Recent audit rows for a rule (what tickets it acted on) + ticket headlines. */
export function useAutomationRuleRuns(ruleId: string | null) {
  return useQuery({
    queryKey: ['automation-rule-runs', ruleId],
    enabled: !!ruleId,
    staleTime: 10_000,
    queryFn: async (): Promise<AutomationRuleRunWithTicket[]> => {
      const { data, error } = await supabase
        .from('automation_rule_runs')
        .select(
          '*, ticket:tickets(id,subject,sender_email,status,priority,assignee_slack_id)',
        )
        .eq('rule_id', ruleId!)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return (data ?? []) as unknown as AutomationRuleRunWithTicket[];
    },
  });
}

/** Bump a rule's applied counter after a bulk run (read-modify-write; bulk
 *  runs are admin-serial so contention is a non-issue). */
export function useBumpAutomationApplied() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ruleId, by }: { ruleId: string; by: number }): Promise<void> => {
      const { data } = await supabase
        .from('automation_rules')
        .select('applied_count')
        .eq('id', ruleId)
        .single();
      const cur = (data?.applied_count as number) ?? 0;
      const { error } = await supabase
        .from('automation_rules')
        .update({ applied_count: cur + by, last_applied_at: new Date().toISOString() })
        .eq('id', ruleId);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['automation-rules'] }),
  });
}

/** Global automation kill switch (company_settings.automations_enabled). */
export function useAutomationsEnabled() {
  return useQuery({
    queryKey: ['automations-enabled'],
    staleTime: 30_000,
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await supabase
        .from('company_settings')
        .select('automations_enabled')
        .eq('id', 1)
        .maybeSingle();
      if (error) throw error;
      // Default ON if the row/column is somehow absent (matches DB default).
      return (data?.automations_enabled ?? true) as boolean;
    },
  });
}

export function useSetAutomationsEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean): Promise<void> => {
      const { error } = await supabase
        .from('company_settings')
        .update({ automations_enabled: enabled, updated_at: new Date().toISOString() })
        .eq('id', 1);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automations-enabled'] });
    },
  });
}

/** Lightweight ticket set for the dry-run preview + bulk-apply match list.
 *  RLS scopes this to the admin's accessible inboxes automatically. body maps
 *  from `body_first` (the first inbound message) so body conditions work on
 *  existing tickets the same way the pipeline matches the incoming email. */
export interface RulePreviewTicket {
  id: string;
  subject: string | null;
  sender_email: string | null;
  body: string | null;
  priority: string | null;
  category: string | null;
  sentiment: string | null;
  is_existing_client: boolean | null;
  client_tier: string | null;
  intent_summary: string | null;
  inbox_id: string | null;
  status: string | null;
  created_at: string;
  // Extra mutable fields captured as before_state for revert.
  assignee_slack_id: string | null;
  snoozed_until: string | null;
  archived_at: string | null;
  resolved_at: string | null;
}

export function useRulePreviewTickets() {
  return useQuery({
    queryKey: ['rule-preview-tickets'],
    staleTime: 60_000,
    queryFn: async (): Promise<RulePreviewTicket[]> => {
      // pageAll: `.limit(5000)` was silently cut at 1,000, so the preview only
      // ever tested a rule against the newest thousand tickets.
      const data = await pageAll<Record<string, unknown>>(
        (from, to) =>
          supabase
            .from('tickets')
            .select(
              'id,subject,sender_email,body_first,priority,category,sentiment,is_existing_client,client_tier,intent_summary,inbox_id,status,created_at,assignee_slack_id,snoozed_until,archived_at,resolved_at',
            )
            .order('created_at', { ascending: false })
            .order('id')
            .range(from, to),
        { maxRows: 5000 },
      );
      return data.map((t) => {
        const row = t as Record<string, unknown>;
        return {
          id: row.id as string,
          subject: (row.subject as string) ?? null,
          sender_email: (row.sender_email as string) ?? null,
          body: (row.body_first as string) ?? null,
          priority: (row.priority as string) ?? null,
          category: (row.category as string) ?? null,
          sentiment: (row.sentiment as string) ?? null,
          is_existing_client: (row.is_existing_client as boolean) ?? null,
          client_tier: (row.client_tier as string) ?? null,
          intent_summary: (row.intent_summary as string) ?? null,
          inbox_id: (row.inbox_id as string) ?? null,
          status: (row.status as string) ?? null,
          created_at: row.created_at as string,
          assignee_slack_id: (row.assignee_slack_id as string) ?? null,
          snoozed_until: (row.snoozed_until as string) ?? null,
          archived_at: (row.archived_at as string) ?? null,
          resolved_at: (row.resolved_at as string) ?? null,
        };
      });
    },
  });
}

/** Mark a bulk-apply run as reverted (after the reverting actions succeed). */
export function useMarkRunReverted() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ runId, revertedBy }: { runId: string; revertedBy: string | null }): Promise<void> => {
      const { error } = await supabase
        .from('automation_rule_runs')
        .update({ reverted_at: new Date().toISOString(), reverted_by: revertedBy })
        .eq('id', runId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automation-rule-runs'] });
    },
  });
}

// ── Access control (migration 078) ─────────────────────────────────────────
// The database enforces all of this; these hooks are what the Team, Roles and
// Access pages (and the permission gates in the UI) read and write through.

export type AccessRole = Tables['roles']['Row'];
export type RolePermissionRow = Tables['role_permissions']['Row'];
export type AccessAuditRow = Tables['access_audit_log']['Row'];

/** The signed-in member's effective access (null for a non-member). Fresh, not
 *  the localStorage-cached team member: a demotion or expiry shows up within a
 *  minute, or on the next focus. */
export function useMyAccess() {
  return useQuery({
    queryKey: ['my-access'],
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<AccessPayload | null> => {
      const { data, error } = await supabase.rpc('my_access');
      if (error) throw error;
      return parseAccess(data);
    },
  });
}

export function useRoles() {
  return useQuery({
    queryKey: ['roles'],
    staleTime: 15_000,
    queryFn: async (): Promise<AccessRole[]> => {
      const { data, error } = await supabase
        .from('roles')
        .select('*')
        .order('kind', { ascending: true })
        .order('is_system', { ascending: false })
        .order('name', { ascending: true });
      if (error) throw error;
      return (data ?? []) as AccessRole[];
    },
  });
}

/** role id -> permission keys it holds. */
export function useRolePermissionMap() {
  return useQuery({
    queryKey: ['role-permissions'],
    staleTime: 15_000,
    queryFn: async (): Promise<Map<string, string[]>> => {
      const { data, error } = await supabase
        .from('role_permissions')
        .select('role_id, permission_key');
      if (error) throw error;
      const map = new Map<string, string[]>();
      for (const row of (data ?? []) as Array<{ role_id: string; permission_key: string }>) {
        const list = map.get(row.role_id);
        if (list) list.push(row.permission_key);
        else map.set(row.role_id, [row.permission_key]);
      }
      return map;
    },
  });
}

/** Every inbox grant (admins see all, others only their own: RLS). */
export function useAllInboxGrants() {
  return useQuery({
    queryKey: ['inbox-grants-all'],
    staleTime: 10_000,
    queryFn: async (): Promise<TeamInboxAccessRow[]> => {
      const { data, error } = await supabase.from('team_inbox_access').select('*');
      if (error) throw error;
      return (data ?? []) as TeamInboxAccessRow[];
    },
  });
}

export function useAccessAudit(
  opts: { targetSlackId?: string | null; limit?: number; enabled?: boolean } = {},
) {
  const limit = opts.limit ?? 100;
  return useQuery({
    queryKey: ['access-audit', opts.targetSlackId ?? null, limit],
    enabled: opts.enabled ?? true,
    staleTime: 10_000,
    queryFn: async (): Promise<AuditRow[]> => {
      let q = supabase
        .from('access_audit_log')
        .select('*')
        .order('at', { ascending: false })
        .limit(limit);
      if (opts.targetSlackId) q = q.eq('target_slack_id', opts.targetSlackId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as unknown as AuditRow[];
    },
  });
}

export interface WhoCanRow {
  slack_user_id: string;
  name: string;
  email: string;
  via: 'workspace_role' | 'all_inboxes' | 'inbox_grant';
  role_name: string;
  expires_at: string | null;
}

export function useWhoCan(perm: string | null, inboxId: string | null) {
  return useQuery({
    queryKey: ['who-can', perm, inboxId],
    enabled: !!perm,
    staleTime: 10_000,
    queryFn: async (): Promise<WhoCanRow[]> => {
      const { data, error } = await supabase.rpc('who_can', {
        p_perm: perm as string,
        ...(inboxId ? { p_inbox: inboxId } : {}),
      });
      if (error) throw error;
      return (data ?? []) as WhoCanRow[];
    },
  });
}

function invalidateAccess(qc: QueryClient) {
  for (const key of [
    'team-members',
    'my-team-member',
    'inbox-grants-all',
    'team-inbox-access',
    'my-inbox-access',
    'my-access',
    'access-audit',
    'who-can',
    'gmail-inboxes',
  ]) {
    qc.invalidateQueries({ queryKey: [key] });
  }
}

export interface InboxRoleInput {
  inbox_id: string;
  /** null revokes the grant */
  role_id: string | null;
  expires_at?: string | null;
}

/** Invite a member with a workspace role and inbox roles, atomically. */
export function useInviteMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      slack_user_id: string;
      email: string;
      name: string;
      workspace_role_id: string;
      access_expires_at?: string | null;
      inbox_roles?: InboxRoleInput[];
      skills?: string[];
      ghl_user_id?: string | null;
    }): Promise<string> => {
      const { data, error } = await supabase.rpc('invite_member', {
        p_slack_user_id: input.slack_user_id,
        p_email: input.email,
        p_name: input.name,
        p_workspace_role_id: input.workspace_role_id,
        p_access_expires_at: input.access_expires_at ?? undefined,
        p_inbox_roles: (input.inbox_roles ?? []).map((r) => ({
          inbox_id: r.inbox_id,
          role_id: r.role_id,
          expires_at: r.expires_at ?? null,
        })),
        p_skills: input.skills ?? [],
        p_ghl_user_id: input.ghl_user_id ?? undefined,
      });
      if (error) throw error;
      return String(data);
    },
    onSuccess: () => invalidateAccess(qc),
  });
}

/** Change a member's role / expiry / active flag and inbox roles in one go. */
export function useSetMemberAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      slack_user_id: string;
      workspace_role_id?: string | null;
      access_expires_at?: string | null;
      clear_expiry?: boolean;
      active?: boolean | null;
      inbox_roles?: InboxRoleInput[] | null;
    }): Promise<void> => {
      const { error } = await supabase.rpc('set_member_access', {
        p_slack_user_id: input.slack_user_id,
        p_workspace_role_id: input.workspace_role_id ?? undefined,
        p_access_expires_at: input.access_expires_at ?? undefined,
        p_clear_expiry: input.clear_expiry ?? false,
        p_active: input.active ?? undefined,
        p_inbox_roles: input.inbox_roles
          ? input.inbox_roles.map((r) => ({
              inbox_id: r.inbox_id,
              role_id: r.role_id,
              expires_at: r.expires_at ?? null,
            }))
          : undefined,
      });
      if (error) throw error;
    },
    onSuccess: () => invalidateAccess(qc),
  });
}

/** Create or edit a custom role and replace its permissions (diffed). */
export function useSaveRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      name: string;
      description: string;
      kind: 'workspace' | 'inbox';
      permissions: string[];
    }): Promise<string> => {
      let roleId = input.id;
      if (roleId) {
        const { error } = await supabase
          .from('roles')
          .update({ name: input.name.trim(), description: input.description.trim() })
          .eq('id', roleId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('roles')
          .insert({ name: input.name.trim(), description: input.description.trim(), kind: input.kind })
          .select('id')
          .single();
        if (error) throw error;
        roleId = (data as { id: string }).id;
      }

      const { data: existing, error: exErr } = await supabase
        .from('role_permissions')
        .select('permission_key')
        .eq('role_id', roleId);
      if (exErr) throw exErr;
      const have = new Set(((existing ?? []) as Array<{ permission_key: string }>).map((r) => r.permission_key));
      const want = new Set(input.permissions);
      const toAdd = [...want].filter((k) => !have.has(k));
      const toRemove = [...have].filter((k) => !want.has(k));

      if (toRemove.length > 0) {
        const { error } = await supabase
          .from('role_permissions')
          .delete()
          .eq('role_id', roleId)
          .in('permission_key', toRemove);
        if (error) throw error;
      }
      if (toAdd.length > 0) {
        const { error } = await supabase.from('role_permissions').insert(
          toAdd.map((permission_key) => ({
            role_id: roleId as string,
            role_kind: input.kind,
            permission_key,
            permission_scope: input.kind,
          })),
        );
        if (error) throw error;
      }
      return roleId as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      qc.invalidateQueries({ queryKey: ['role-permissions'] });
      qc.invalidateQueries({ queryKey: ['access-audit'] });
      qc.invalidateQueries({ queryKey: ['my-access'] });
    },
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (roleId: string): Promise<void> => {
      const { error } = await supabase.from('roles').delete().eq('id', roleId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles'] });
      qc.invalidateQueries({ queryKey: ['role-permissions'] });
      qc.invalidateQueries({ queryKey: ['access-audit'] });
    },
  });
}
